import {
	AfterViewInit,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ElementRef,
	Inject,
	Input,
	OnDestroy,
	OnInit,
	ViewChild,
} from "@angular/core";
import {
	AbstractControl,
	UntypedFormArray,
	UntypedFormBuilder,
	UntypedFormControl,
	UntypedFormGroup,
	Validators,
} from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { MatStepper } from "@angular/material/stepper";
import { ActivatedRoute, Router } from "@angular/router";
import moment from "moment";
import { NgxIndexedDBService } from "ngx-indexed-db";
import { combineLatest } from "rxjs";
import { filter as rxFilter, take, takeUntil } from "rxjs/operators";
import { ETRAINING_DB } from "src/app/app.module";
import { isEts1SessionV2 } from "src/app/cbta-web/ets1/session/ets1-session-form.utils";
import { OfflineRatingService } from "src/app/cbta-web/services/offline-rating-calculation.service";
import { CONSTANT } from "src/app/constant";
import { CandidateRole, RouteType } from "src/app/models/enum";
import {
	AssessmentConfigRes,
	CandidateSummary,
	CrewRoleAssignment,
	DraftItem,
	FinalRatingsData,
	ManeuverOutcomeDraft,
	Maneuvers,
	OBAttempt,
	OBComments,
	RatingCandidateForm,
	Row,
	StartRatingForm,
	StartRatingResp,
	StartRatingRespData,
	TemCompetencyOutcomeDraft,
	TS1Form,
	TS1FormResponseJSON,
} from "src/app/models/models";
import { CbtaModalComponent } from "src/app/shared/components/cbta-modal/cbta-modal.component";
import { HttpService } from "src/app/shared/services/http.service";
import { LoaderService } from "src/app/shared/services/loader.service";
import { ProfileService } from "src/app/shared/services/profile.service";
import { RoleService } from "src/app/shared/services/role.service";
import { SharedService } from "src/app/shared/services/shared.service";
import * as uuid from "uuid";
import { CustomValidationService } from "../../../services/custom-validation.service";
import { ContainerRatingsService } from "../services/base/container-ratings.services";
import {
	attachSessionFormControls,
	createCandidateGroup,
	createCandidateRatingForm,
	createCandidateSignoffForm,
	createRatingFormContainer,
	createSignOffFormContainer,
	createTrainingInfoForm,
	prefillSessionRemarks,
} from "../shared-v2/base/form.factories";
import { TsRatingFormV2Base } from "../shared-v2/base/ts-rating-form-v2.base";
import {
	buildFlightSimulatorDetailsPayload,
	flightSimulatorDetailsForFormPatch,
	patchFlightSimulatorDetailsOntoForm,
} from "../shared-v2/flight-simulator-details.payload";
import {
	hydrateFormDetailsConfigVersion,
	hydrateFormReminderNotes,
	hydrateFormSessionDetailsRemarks,
	resolveFormDetailsForRating,
} from "../shared-v2/utils/form-config-version.utils";
import {
	applyDisplayObsLastActions,
	applyDraftAddRemoveObEvent,
	applyLatestNaDisabledStateFromAddRemoveOb,
	buildCategoriesFromData,
	buildSectionsFromTemplateV2,
	coerceBool,
	filterSelectedOBsExcludingReset,
	getObCellControl,
	parseObLabel,
	parseObsCode,
	persistTs1V3ObUndoBaselines,
	reapplyV3ManeuverRepeatFormState,
	refreshManeuverRepeatBaselines,
	resolveV2ObRoleSlot,
	seedAndPersistTs1V3ObUndoBaselines,
	seedInitialObByKeyFromObservationForms,
	shouldReplayDraftAddRemoveObEvent,
} from "../shared-v2/utils/observation.utils";
import { buildTs1PreviewCandidateDetails } from "./preview/ts1-preview-summary-details.utils";
import { Ts1RatingStep2V2Component } from "./steps/step2/ts1-rating-step2-v2/ts1-rating-step2-v2.component";
import { Ts1RatingStep3ReviewComponent } from "./steps/step3/ts1-rating-step3-review/ts1-rating-step3-review.component";
import {
	buildTs1ApplicableCountsFromAddRemoveOb,
	buildTs1CompetencyApplicableCounts,
} from "./utils/ts1-competency-scoring.utils";
import {
	applyPreDefinedFlagsFromConfig,
	collectAssessmentConfigObPresets,
	normalizeConfigCandidateRole,
	resolveAssessmentConfigManeuverCode,
	resolveObservationConfigCode,
} from "./utils/ts1-predefined-ob.utils";
import {
	applyManeuverOutcomeNaDisabledToEvents,
	mergeTs1V3ObEventsForRatingCalc,
} from "./utils/ts1-rating-ab-ob-merge.utils";
import { rebuildDisplaySnapshots } from "./utils/ts1-rating-display-snapshot.utils";
import { canSendTs1V3ToCandidate } from "./utils/ts1-rating-form-v3-signoff.utils";
import {
	applyManeuverOutcomeNaToObservationForms,
	buildTs1V3CandidateSummaryComments,
	DEFAULT_OB_COMMENTS_ENABLED,
	findManeuverFormForOutcomeSlot,
	getSafeCandidateRating,
	mergeTs1V3SelectedOBsForSummary,
	SHOW_ENABLE_COMMENT_TOGGLE,
	syncManeuverOutcomeNaCascade,
} from "./utils/ts1-rating-ob-interaction";
import {
	appendOutcomeToSelectedOBs,
	applyOutcomeSummaryCommentToDraft,
	applyTemCompetencySummaryCommentToDraft,
	ensureManeuverOutcomeHistoryInSelectedOBs,
	maneuverOutcomeHasActivity,
	restoreV3OutcomesFromSelectedOBs,
} from "./utils/ts1-rating-outcome-interaction";
import {
	buildTs1ReviewCompetencyBlocks,
	buildTs1ReviewManeuverRows,
	isCompetencyManeuverCode,
	TS1_REVIEW_COMPETENCY_ORDER,
	Ts1ReviewCompetencyBlock,
	Ts1ReviewManeuverRow,
} from "./utils/ts1-rating-review.utils";
import {
	scrollPageContainerToCandidateHeaderWhenReady,
	scrollPageContainerToStep3ReviewTargetWhenReady,
	step3ReviewScrollTargetId,
} from "./utils/ts1-rating-scroll.utils";
import { sortSessionGroupDetailsAlphabetically } from "./utils/ts1-rating-session-sort.utils";
import { resolveStep1ToStep2Action } from "./utils/ts1-rating-step1-flow.utils";
import {
	getStep2ManeuverOutcomeIncompleteReasons,
	getStep3CoreReviewIncompleteItems,
	getStep3MissingObComments,
	groupStep3CoreReviewIncompleteItems,
	isStep2ManeuverOutcomesComplete,
	isStep3CoreReviewComplete,
	isStep3ReviewComplete,
	Step3MissingObComment,
	Step3ReviewIncompleteGroup,
	Step3ReviewIncompleteItem,
} from "./utils/ts1-rating-step3-validation.utils";
import { signOffNoteForAutosave } from "./utils/ts1-sign-off-note-draft.utils";
import { normalizeTemCompetencyOutcomes } from "./utils/ts1-tem-competency-slots.utils";

@Component({
	selector: "app-ts1-rating-form-v3",
	templateUrl: "./ts1-rating-form-v3.component.html",
	styleUrls: ["./ts1-rating-form-v3.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class Ts1RatingFormV3Component extends TsRatingFormV2Base implements OnInit, AfterViewInit, OnDestroy {
	// Optional inputs from parent containers.
	@Input() data?: { data: any };
	@Input() sessionDetails?: any;
	@Input() observationDetails?: any;

	// View references.
	@ViewChild("baseContainer") baseContainer?: ElementRef<HTMLElement>;
	@ViewChild("stepper") override stepper!: MatStepper;
	@ViewChild(Ts1RatingStep2V2Component) private step2V2?: Ts1RatingStep2V2Component;
	@ViewChild(Ts1RatingStep3ReviewComponent) private step3Review?: Ts1RatingStep3ReviewComponent;

	/** Coalesce deferred Repeat-button sync to avoid NG0100 from ViewChild getters. */
	private repeatDisabledSyncQueued = false;
	/** Debounce Step 3 recommendation / notes draft persists. */
	private signOffDraftSaveTimer: ReturnType<typeof setTimeout> | null = null;
	private static readonly SIGN_OFF_DRAFT_DEBOUNCE_MS = 400;

	/** Cached sticky-header width — live offsetWidth getters cause NG0100 during layout. */
	baseContainerWidthPx = 0;
	private baseContainerResizeObserver?: ResizeObserver;
	private baseContainerWidthSyncQueued = false;
	/** Guards deferred width sync microtasks after destroy. */
	private baseContainerWidthDestroyed = false;

	// Root forms.
	ts1TrainingInfoForm!: UntypedFormGroup;
	ts1RatingForm!: UntypedFormGroup;
	ts1ReviewForm!: UntypedFormGroup;
	ts1SignOffForm!: UntypedFormGroup;

	/**
	 * Synced from step2/OB panel after CD (not a ViewChild getter — avoids NG0100).
	 * Template binds `[disabled]="repeatDisabled"`.
	 */
	repeatDisabled = true;

	// Component-specific collections.
	finalratingArray: FinalRatingsData[] = [];
	candidateSummary: CandidateSummary[] = [];
	override assessmentConfig: AssessmentConfigRes[] = [];
	TS1Form!: TS1Form;
	manueverRaw: any;
	formSessionRemarksByCode: Record<string, string> = {};

	inPreviewSummary: boolean = false;

	signOffMode: "remote" = "remote";

	/** True while Send to Candidate is in flight (flush + promote). */
	isSendingToCandidate = false;

	seenSummary: boolean = false;

	// Cached preview data to avoid recomputing on every change detection
	previewCandidates: any[] = [];
	private reviewPreviewSummaryCache: any[] | null = null;
	private reviewCompetencyBlocksCache = new Map<number, Ts1ReviewCompetencyBlock[]>();
	private step3CoreCompleteCache: boolean | null = null;
	private step3IncompleteReasonsCache: string[] | null = null;
	private step3IncompleteItemsCache: Step3ReviewIncompleteItem[] | null = null;
	/** Expanded checklist of Step 3 gaps blocking Sign off. */
	step3GapsChecklistExpanded = false;
	/** Persistent highlight on the checklist item the instructor jumped to. */
	step3ScrollHighlight: { candidateIndex: number; key: string } | null = null;
	private step2ManeuverCompleteCache: boolean | null = null;
	private step2IncompleteReasonsCache: string[] | null = null;

	/** configVersion 2 admin forms only — routed here from container; Step 2 always uses Figma v2 layout. */
	/** Crew role per fixed candidate slot (index 0 = left header, index 1 = right header). */
	candidateCrewRoles: CandidateRole[] = [CandidateRole.PF, CandidateRole.PM];
	maneuverOutcomes: ManeuverOutcomeDraft[] = [];
	temCompetencyOutcomes: TemCompetencyOutcomeDraft[] = [];
	/** Per-candidate Step 1 accordion expanded state — preserved when adding candidates. */
	candidatePanelExpanded: boolean[] = [];

	private static readonly DEFAULT_CREW_ROLES: CandidateRole[] = [CandidateRole.PF, CandidateRole.PM];

	get pfCandidateIndex(): number {
		return this.candidateCrewRoles.findIndex((r) => r === CandidateRole.PF);
	}

	get pmCandidateIndex(): number {
		return this.candidateCrewRoles.findIndex((r) => r === CandidateRole.PM);
	}

	constructor(
		protected override fb: UntypedFormBuilder,
		protected override httpService: HttpService,
		public cd: ChangeDetectorRef,
		protected override loadingService: LoaderService,
		public sharedService: SharedService,
		public router: Router,
		protected override route: ActivatedRoute,
		protected override roleService: RoleService,
		private cvs: CustomValidationService,
		protected override profileService: ProfileService,
		protected override dialog: MatDialog,
		protected override containerRatingsService: ContainerRatingsService,
		protected override offlineRatingService: OfflineRatingService,
		@Inject(ETRAINING_DB) private readonly eTrainingDb: NgxIndexedDBService,
	) {
		super();
		this.initializeComponent();
	}

	// Map abstract getters to concrete root forms.
	protected override get trainingInfoForm(): UntypedFormGroup {
		return this.ts1TrainingInfoForm;
	}
	protected override get ratingRootForm(): UntypedFormGroup {
		return this.ts1RatingForm;
	}
	protected override get signOffRootForm(): UntypedFormGroup {
		return this.ts1SignOffForm;
	}

	get showEnableCommentToggle(): boolean {
		return SHOW_ENABLE_COMMENT_TOGGLE && this.stepper?.selectedIndex === 1;
	}

	get formTopPadding(): string {
		// CSS tokens already subtract page-container py-10 so Enable Comment sits flush
		// above the candidate header after refresh / offline draft restore.
		return this.showEnableCommentToggle ? "var(--ts1-step2-form-padding-top)" : "var(--ts1-step1-form-padding-top)";
	}

	get obCommentsEnabled(): boolean {
		return getSafeCandidateRating(this.containerRatingsService.draft).obCommentsEnabled ?? DEFAULT_OB_COMMENTS_ENABLED;
	}

	onObCommentsEnabledChange(enabled: boolean): void {
		if (!enabled && this.obCommentsEnabled) {
			const dialogRef = this.dialog.open(CbtaModalComponent, {
				panelClass: "error-modal",
				height: "auto",
				maxHeight: "90vh",
				width: "520px",
				autoFocus: "dialog",
				hasBackdrop: true,
				data: {
					modalName: "disableCommentPromptsModal",
					modalTitle: "Disable Comment Prompts?",
					modalBtnConfirmText: "Turn Off",
					modalBtnCancelText: "Cancel",
				},
			});
			dialogRef.afterClosed().subscribe((result: { close?: boolean } | undefined) => {
				if (result?.close) {
					this.applyObCommentsEnabled(false);
				}
			});
			return;
		}
		this.applyObCommentsEnabled(enabled);
	}

	private applyObCommentsEnabled(enabled: boolean): void {
		const cr = getSafeCandidateRating(this.containerRatingsService.draft);
		void this.containerRatingsService.updateSelectedDraft({
			candidateRating: { ...cr, obCommentsEnabled: enabled },
			lastUpdatedDate: new Date().toISOString(),
			continuePage: 2,
		});
		this.step2V2?.refreshObPanelStars();
		this.queueRepeatDisabledSync();
		this.cd.markForCheck();
	}

	expandAllObSections(): void {
		this.step2V2?.expandAllObSections();
	}

	collapseAllObSections(): void {
		this.step2V2?.collapseAllObSections();
		this.scrollToTop();
	}

	/**
	 * Immediate scroll to the bottom of the page container.
	 */
	scrollToBottom(): void {
		const element = document.getElementById("cbta-web-page-container") as HTMLElement | null;
		if (!element) return;
		element.scrollTop = element.scrollHeight;
	}

	// Connectivity flags used by the UI.
	get isOnline(): boolean {
		return navigator.onLine;
	}
	get isSynced(): boolean {
		return this.containerRatingsService.isSynced;
	}

	// Frequently accessed controls.
	get formType() {
		return this.ts1TrainingInfoForm.get("formType")!;
	}
	get formName() {
		return this.ts1TrainingInfoForm.get("formName")!;
	}
	get syllabusStartDate() {
		return this.ts1TrainingInfoForm.get("syllabusStartDate")!;
	}
	get syllabusEndDate() {
		return this.ts1TrainingInfoForm.get("syllabusEndDate")!;
	}
	get flightSimulation() {
		return this.ts1TrainingInfoForm.controls["flightSimulation"] as UntypedFormGroup;
	}
	get candidatesArray() {
		return this.candidates;
	}
	get ratingFormArray() {
		return this.ratingForm;
	}
	get signOffFormArray() {
		return this.signOffForm;
	}
	get airRoute() {
		return this.flightSimulation.get("airRoute");
	}

	/**
	 * Load data from the three sources required to render the form:
	 * - form metadata
	 * - observations
	 * - session template
	 * After all are available, build forms, populate values, and attach autosave.
	 */
	ngOnInit(): void {
		this.initialize();
	}

	initialize() {
		combineLatest([
			this.containerRatingsService.getFormData(),
			this.containerRatingsService.getObservationData(),
			this.containerRatingsService.getSessionData(),
			this.containerRatingsService.getManueversRawData(),
		])
			.pipe(
				rxFilter(
					([formData, observationDetails, sessionDetails, manueverRaw]) =>
						!!formData &&
						Array.isArray(observationDetails) &&
						observationDetails.length > 0 &&
						!!sessionDetails &&
						!!manueverRaw,
				),
				take(1),
			)
			.subscribe(async ([formData, observationDetails, sessionDetails, manueverRaw]) => {
				console.log("formData", formData);
				const formId =
					this.formId ||
					(formData as TS1FormResponseJSON)?.id ||
					this.containerRatingsService.draft?.formDetails?.id ||
					this.containerRatingsService.draft?.trainingInformation?.formId;
				let hydrated = await hydrateFormDetailsConfigVersion(
					formData as TS1FormResponseJSON,
					formId,
					this.eTrainingDb,
					this.httpService,
				);
				hydrated = await hydrateFormSessionDetailsRemarks(hydrated, formId, this.httpService);
				hydrated = await hydrateFormReminderNotes(hydrated, formId, this.httpService, this.eTrainingDb);
				hydrated = resolveFormDetailsForRating(hydrated) ?? hydrated;

				this.manueverRaw = manueverRaw;
				this.initializeAllForms();
				this.loadForm(hydrated, observationDetails as any, sessionDetails as any);
				this.attachFormListenersOnce();
			});
	}

	togglePreviewSummary() {
		this.inPreviewSummary = !this.inPreviewSummary;
		if (this.inPreviewSummary) {
			this.previewCandidates = this.getPreviewSummaryData();
			try {
				sessionStorage.setItem("quick_summary", JSON.stringify(this.previewCandidates));
			} catch {}
		}
	}

	onPreviewFinished(done: boolean) {
		this.seenSummary = done;
		this.togglePreviewSummary();
		if (!this.inPreviewSummary && this.continuePage >= 4) {
			setTimeout(() => {
				this.forceSelectStep(3);
				this.cd.detectChanges();
			});
		}
		this.scrollToTop();
	}

	/**
	 * Create the three step root forms.
	 */
	private initializeAllForms(): void {
		this.ts1TrainingInfoForm = createTrainingInfoForm(this.fb);
		this.ts1RatingForm = createRatingFormContainer(this.fb);
		this.ts1ReviewForm = this.fb.group({});
		this.ts1SignOffForm = createSignOffFormContainer(this.fb);
	}

	protected override attachFormListenersOnce(): void {
		super.attachFormListenersOnce();
		this.ratingRootForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.queueRepeatDisabledSync());
	}

	/**
	 * Populate Step 1 fields, prepare candidate array, and set up permissions.
	 * If a draft was requested, load and apply it after options are ready.
	 */
	loadForm(
		resp: TS1FormResponseJSON,
		manuver: Array<{ name?: string; observation_category?: { code?: string } }> = [],
		session: any[] = [],
	): void {
		this.rawFormData = resolveFormDetailsForRating(resp) ?? resp;

		this.candidates.clear();
		this.candidatePanelExpanded = [];
		const sortedSessions = sortSessionGroupDetailsAlphabetically(session);
		this.sessionKeys = sortedSessions;

		const maneuvers: Maneuvers[] = manuver.length
			? [
					{
						maneuver: {
							code: resp.assessmentConfig?.[0]?.maneuverType?.code ?? "",
							description: resp.assessmentConfig?.[0]?.maneuverType?.description ?? "",
						},
						...buildCategoriesFromData(manuver, resp.assessmentConfig?.[0]?.maneuverType?.code),
					} as Maneuvers,
				]
			: [];

		this.observationData = maneuvers;

		this.sessionConfigs = sortedSessions;
		this.assessmentConfig = [...resp.assessmentConfig];

		const date = this.containerRatingsService?.getInitialDate();

		this.formType.patchValue(resp.formType, { emitEvent: false });
		this.formName.patchValue(resp.name, { emitEvent: false });

		this.syllabusStartDate.patchValue(
			moment(new Date(date ?? resp.startDate)).format(CONSTANT.DATE_FORMAT.FORMS_BE_DATE_FORMAT),
			{ emitEvent: false },
		);

		this.syllabusEndDate.patchValue(
			moment(new Date(date ?? resp.endDate)).format(CONSTANT.DATE_FORMAT.FORMS_BE_DATE_FORMAT),
			{ emitEvent: false },
		);

		this.formSessionDetails = resp.sessionDetails.map((s) => s.code);
		this.formSessionRemarksByCode = Object.fromEntries(
			(resp.sessionDetails ?? []).map((s) => [s.code, s.selectionRemarks?.trim() ?? ""]).filter(([, remark]) => remark),
		);

		const first = createCandidateGroup(this.fb, this.cvs, CONSTANT.FORM_TYPE.ts_1, {
			splitAircraftSimulatorRegistration: true,
			ts1V3FlightDetails: true,
		});
		attachSessionFormControls(first, this.fb, this.sessionKeys, this.formSessionDetails);
		this.applyFormTemplateSessionRemarks(first);
		this.candidates.push(first);
		this.candidatePanelExpanded = [true];

		if (!this.roleService.isAdmin) {
			this.formName.disable({ emitEvent: false });
			this.disableForm = true;
		}

		if (this.draftId) {
			this.loadDraftValues(this.draftId);
		}

		if (!this.isBootstrapping) {
			this.ts1TrainingInfoForm.updateValueAndValidity();
		}

		this.loadingService.setLoading(false);
	}

	/**
	 * Pre-fill Step 1 session remark inputs from admin form template defaults.
	 */
	private applyFormTemplateSessionRemarks(group: UntypedFormGroup): void {
		prefillSessionRemarks(group, this.sessionKeys, this.formSessionRemarksByCode, this.formSessionDetails);
	}

	/**
	 * Assemble the Step 1 payload to begin a rating session (online or offline).
	 */
	getStartRatingData(): StartRatingForm {
		const candidates: RatingCandidateForm[] = [];

		this.candidates.controls.forEach((c: any) => {
			const sessionDetailsRaw = c.get("sessionDetails")?.getRawValue();
			const grouped = this.buildSessionSelection(sessionDetailsRaw);
			const sessionRemarksRaw = c.get("sessionRemarks")?.getRawValue();
			const remarksGrouped = this.buildSessionRemarks(sessionRemarksRaw);
			const fs = c.get("flightSimulatorDetails")?.getRawValue() || {};

			candidates.push({
				name: c.value?.name,
				staffNumber: c.value?.staffNumber,
				licenseNumber: c.value?.licenseNo,
				licenseType: c.value?.licenseType,
				rank: c.value?.rank?.code,
				trainingProfile: c.value?.pilotProfile?.code,
				trainingProfileOther: c.value?.trainingProfileOther,
				licenseExpiryDate: moment(c.value?.licenseExpiryDate).format("YYYY-MM-DD"),
				sessionDetails: [grouped],
				sessionRemarks: [remarksGrouped],
				flightSimulatorDetails: buildFlightSimulatorDetailsPayload(fs),
			});
		});

		return {
			formId: this.formId,
			assessmentDate: moment(this.syllabusStartDate.value).format("YYYY-MM-DD"),
			candidates,
			instructorId: this.roleService.userDetails?.id ?? null,
		};
	}

	/**
	 * Build the trainingInformation structure, preserving candidate ids and primary flag.
	 */
	protected override getTrainingInformation(): StartRatingForm | undefined {
		const startRatingData = this.getStartRatingData();
		const existing: any[] = this.containerRatingsService?.draft?.trainingInformation?.candidates ?? [];

		// Build maps for robust lookup
		const byStaff: Map<string, any> = new Map();
		const byFingerprint: Map<string, any> = new Map(); // name+license fallback

		for (const e of existing) {
			if (e?.staffNumber) byStaff.set(String(e.staffNumber), e);
			const fp = `${(e?.name ?? "").trim().toLowerCase()}|${(e?.licenseNumber ?? "").trim().toLowerCase()}`;
			if (fp.trim() !== "|") byFingerprint.set(fp, e);
		}

		const currentStaffNumbers = new Set(
			startRatingData.candidates.map((c) => String(c.staffNumber ?? "")).filter(Boolean),
		);

		const candidates = startRatingData.candidates.map((c, i) => {
			// try: 1) staffNumber, 2) rating-form candidateId, 3) fingerprint (name+license),
			// 4) positional when same slot (and old staffNumber not moved to another slot), 5) else new uuid
			const staffKey = String(c.staffNumber ?? "");
			const fpKey = `${(c.name ?? "").trim().toLowerCase()}|${(c.licenseNumber ?? "").trim().toLowerCase()}`;

			let prev = (staffKey && byStaff.get(staffKey)) || undefined;

			if (!prev) {
				const ratingFormId = this.candidateIdFromRatingFormIndex(i);
				if (ratingFormId) {
					prev = existing.find((e) => e?.candidateId === ratingFormId) ?? { candidateId: ratingFormId };
				}
			}

			if (!prev && fpKey.trim() !== "|") {
				prev = byFingerprint.get(fpKey);
			}

			if (
				!prev &&
				existing[i] &&
				(!existing[i]?.staffNumber ||
					existing[i]?.staffNumber === c.staffNumber ||
					!currentStaffNumbers.has(String(existing[i].staffNumber)))
			) {
				prev = existing[i];
			}

			return {
				...c,
				candidateId: prev?.candidateId ?? uuid.v4(),
				isPrimary: typeof prev?.isPrimary === "boolean" ? prev.isPrimary : i === 0,
			};
		});

		return {
			...startRatingData,
			candidates,
			// keep assessmentDate consistent with other places (string iso)
			assessmentDate: moment(this.syllabusStartDate.value).format("YYYY-MM-DD"),
		};
	}

	/**
	 * Offline response shape for building Step 2 without a server call.
	 */
	getStartRatingOfflineResponse(trainingInformation?: StartRatingForm): StartRatingResp {
		const id = this.assessmentOfflineId ?? uuid.v4();
		this.assessmentOfflineId = id;

		const offlineCandidates =
			trainingInformation?.candidates?.map((d, i) => {
				return {
					id: d.candidateId!,
					name: d.name,
					staffNumber: d.staffNumber,
					isPrimary: d.isPrimary ?? i === 0,
				};
			}) ?? [];

		return {
			data: {
				candidates: offlineCandidates,
				traineeAssessmentId: id,
				assessmentConfig: this.assessmentConfig?.map(({ maneuverType, observations, assessmentId }) => {
					return {
						assessmentId,
						maneuver: { ...maneuverType, id: maneuverType.id ?? "" },
						observations: observations?.map((ob) => ({ ...ob, observationId: ob.id })),
					};
				}),
			},
		};
	}

	/**
	 * Extract Step 3 sign-off fields into a draft-friendly structure.
	 */
	protected override collectSignOffDetailsFromForm() {
		const out: any = { candidateSignOffDetails: {} };
		const savedByCandidate = this.containerRatingsService.draft?.signOffDetails?.candidateSignOffDetails ?? {};
		const controls = this.signOffForm?.controls ?? [];
		const defaultTrainerName = this.roleService.userDetails?.name ?? "";
		for (const control of controls) {
			const row = (control as UntypedFormGroup).getRawValue?.() ?? control.value;
			if (!row?.candidateId) continue;
			const saved = savedByCandidate[row.candidateId] ?? {};
			const generalObservation = signOffNoteForAutosave(control.get("generalObservation"), saved.generalObservation);
			const reminderNotes = signOffNoteForAutosave(control.get("reminderNotes"), saved.reminderNotes);
			out.candidateSignOffDetails[row.candidateId] = {
				...saved,
				ratingA: row.ratingA,
				ratingB: row.ratingB,
				ratingC: row.ratingC,
				finalRating: row.finalRating,
				category: row.category ?? "",
				sessionCheck: row.sessionCheck,
				objectiveCheck: row.objectiveCheck,
				clearanceCheck: row.clearanceCheck,
				instructorLicenseNumber: row.instructorLicenseNumber ?? "",
				useInstructorLicense: !!row.useInstructorLicense,
				trainerName: row.trainerName || saved.trainerName || defaultTrainerName,
				traineeName: row.traineeName ?? "",
				trainerSignature: row.trainerSignature ?? saved.trainerSignature ?? null,
				traineeSignature: row.traineeSignature ?? saved.traineeSignature ?? null,
				candidateIdName: row.candidateId ?? "",
				candidateName: row.traineeName ?? "",
				traineeAssessmentId: this.assessmentOfflineId!,
				...(generalObservation !== undefined ? { generalObservation } : {}),
				...(reminderNotes !== undefined ? { reminderNotes } : {}),
				reminderNoteResponses: row.reminderNoteResponses ?? saved.reminderNoteResponses ?? [],
			};
		}
		return out;
	}

	private syncSignOffFormToDraftMemory(): void {
		if (this.isBootstrapping || !this.signOffForm?.length || !this.containerRatingsService.draft) return;
		const signOffDetails = this.collectSignOffDetailsFromForm();
		if (Object.keys(signOffDetails.candidateSignOffDetails ?? {}).length === 0) return;
		this.containerRatingsService.draft.signOffDetails = signOffDetails;
	}

	private syncExistingRatingFormCandidates(trainingInformation: StartRatingForm): void {
		const sortedCandidates = [...(trainingInformation.candidates ?? [])].sort((a, b) =>
			a.isPrimary === b.isPrimary ? 0 : a.isPrimary ? -1 : 1,
		);
		const activeCandidateIds = new Set(sortedCandidates.map((c) => c?.candidateId).filter((id): id is string => !!id));
		const signOffControls = (this.signOffForm?.controls ?? []) as UntypedFormGroup[];
		const signOffByCandidateId = new Map<string, UntypedFormGroup>();
		for (const ctrl of signOffControls) {
			const id = ctrl.get("candidateId")?.value;
			if (id) signOffByCandidateId.set(id, ctrl);
		}

		sortedCandidates.forEach((cand, idx) => {
			if (!cand?.candidateId) return;
			const rf = this.ratingForm?.at(idx) as UntypedFormGroup | undefined;
			if (rf) {
				rf.get("candidateName")?.patchValue(cand.name ?? "", { emitEvent: false });
				const obs = rf.get("observations") as UntypedFormArray | null;
				obs?.controls?.forEach((obCtrl) => {
					obCtrl.get("candidateId")?.patchValue(cand.candidateId, { emitEvent: false });
				});
			}
			const indexedSf = this.signOffForm?.at(idx) as UntypedFormGroup | undefined;
			const indexedSfCandidateId = indexedSf?.get("candidateId")?.value;
			const sf =
				signOffByCandidateId.get(cand.candidateId) ??
				(indexedSf && (!indexedSfCandidateId || !activeCandidateIds.has(indexedSfCandidateId)) ? indexedSf : undefined);
			if (sf) {
				sf.patchValue(
					{
						candidateId: cand.candidateId,
						traineeName: cand.name ?? "",
						candidateName: cand.name ?? "",
					},
					{ emitEvent: false },
				);
			}
		});
	}

	/**
	 * Step 1 → Step 2 transition.
	 * Validates Step 1, builds rating forms and observations, and persists progress.
	 */
	override async onStepperNext(_stepper: MatStepper): Promise<void> {
		this.gotoTop();

		if (!this.isBootstrapping) {
			this.ts1TrainingInfoForm.updateValueAndValidity();
			this.ts1TrainingInfoForm.markAllAsTouched();
		}

		if (!this.ts1TrainingInfoForm.valid) return;

		const ratingCount = this.ratingForm.controls.length;
		const candidateCount = this.candidates.length;
		const step2Action = resolveStep1ToStep2Action(this.isStep1FormCreated, ratingCount, candidateCount);

		if (step2Action === "resume") {
			await this.resumeRatingSession(_stepper);
			return;
		}
		if (step2Action === "append") {
			await this.appendMissingCandidateRatingForms(_stepper);
			return;
		}

		this.ts1RatingForm = createRatingFormContainer(this.fb);

		if (this.ratingForm.controls.length > 0 && !this.isStep1FormCreated) {
			this.current = 0;
			this.prev = -1;
		}

		this.loadingService.setLoading(true);
		this.continuePage = Math.max(this.continuePage, 2);

		try {
			const trainingInformation = this.getTrainingInformation();
			if (!trainingInformation) {
				this.loadingService.setLoading(false);
				return;
			}

			const offlineResp = this.getStartRatingOfflineResponse(trainingInformation);
			await this.startRatingSuccessOffline(offlineResp);

			this.cd.detectChanges();

			if (!this.isBootstrapping) {
				const draftExtras: Partial<DraftItem> = {
					candidateRating: {
						...getSafeCandidateRating(this.containerRatingsService.draft),
						crewRoleAssignment: {
							rolesByCandidateIndex: [...this.candidateCrewRoles],
						},
						maneuverOutcomes: this.maneuverOutcomes,
					},
				};
				this.saveToDrafts(this.continuePage, { trainingInformation, ...draftExtras }, false).catch((err) =>
					console.error("saveToDrafts failed (non-blocking):", err),
				);
			}
		} catch (err) {
			console.error("onStepperNext failed:", err);
			this.loadingService.setLoading(false);
		}
	}

	/** Step 1 → Step 2 when rating forms already exist (e.g. user clicked Back then Start Rating again). */
	private async resumeRatingSession(stepper: MatStepper): Promise<void> {
		this.gotoTop();
		this.loadingService.setLoading(true);
		this.continuePage = Math.max(this.continuePage, 2);

		try {
			const trainingInformation = this.getTrainingInformation();
			if (!trainingInformation) {
				return;
			}

			this.getCandidateList();
			this.syncExistingRatingFormCandidates(trainingInformation);

			if (!this.isBootstrapping) {
				const draftExtras: Partial<DraftItem> = {
					candidateRating: {
						...getSafeCandidateRating(this.containerRatingsService.draft),
						crewRoleAssignment: {
							rolesByCandidateIndex: [...this.candidateCrewRoles],
						},
						maneuverOutcomes: this.maneuverOutcomes,
					},
				};
				await this.saveToDrafts(this.continuePage, { trainingInformation, ...draftExtras }, false);
			}

			this.stripCompetencyObservationsFromRatingForm();
			refreshManeuverRepeatBaselines(this.ratingForm);
			await this.initRatingValues();

			this.ratingFormLoaded = true;
			this.cd.detectChanges();
			this.step2V2?.resyncSummary();
			this.step2V2?.refreshObPanelStars();
			this.queueRepeatDisabledSync();
			this.goToCandidateRatingStep();
		} catch (err) {
			console.error("resumeRatingSession failed:", err);
		} finally {
			this.loadingService.setLoading(false);
		}
	}

	/**
	 * Step 1 → Step 2 when new candidates were added after an earlier Start Rating
	 * (e.g. rated 1 candidate, Back, add 2nd, Start Rating again).
	 */
	private async appendMissingCandidateRatingForms(stepper: MatStepper): Promise<void> {
		this.gotoTop();
		this.loadingService.setLoading(true);
		this.continuePage = Math.max(this.continuePage, 2);

		try {
			const trainingInformation = this.getTrainingInformation();
			if (!trainingInformation) {
				return;
			}

			const offlineResp = this.getStartRatingOfflineResponse(trainingInformation);
			const startIdx = this.ratingForm.length;
			this.getCandidateList();
			this.syncExistingRatingFormCandidates(trainingInformation);

			if (!this.isBootstrapping) {
				await this.saveToDrafts(this.continuePage, { trainingInformation }, false);
			}

			this.stripCompetencyObservationsFromRatingForm();

			const merged = this.getMergedManeuverRows(offlineResp);
			const respCandidates = offlineResp.data.candidates ?? [];

			for (let i = startIdx; i < this.candidateList.length; i++) {
				const cand = respCandidates[i];
				if (!cand) continue;

				this.ratingForm.push(createCandidateRatingForm(this.fb, this.candidateList[i], this.assessmentOfflineId));
				const observations = this.ratingForm.at(i).get("observations") as UntypedFormArray;

				merged.forEach((m: Row) => {
					observations.push(this.addOBs(m.maneuver, m.maneuverCode, m.assessmentId, m.traineeAssessmentId, cand.id));
				});
			}

			this.buildSelectedOb(offlineResp.data, startIdx);
			this.initCrewRoleIndices();
			refreshManeuverRepeatBaselines(this.ratingForm);
			await this.initRatingValues(startIdx);

			this.ratingFormLoaded = true;
			this.current = 0;
			this.prev = -1;
			this.normalizeCandidateView();
			this.cd.detectChanges();
			this.step2V2?.resyncSummary();
			this.step2V2?.refreshObPanelStars();
			this.queueRepeatDisabledSync();

			if (!this.isBootstrapping) {
				const draftExtras: Partial<DraftItem> = {
					candidateRating: {
						...getSafeCandidateRating(this.containerRatingsService.draft),
						crewRoleAssignment: {
							rolesByCandidateIndex: [...this.candidateCrewRoles],
						},
						maneuverOutcomes: this.maneuverOutcomes,
					},
				};
				await this.saveToDrafts(this.continuePage, { trainingInformation, ...draftExtras }, false);
			}

			this.goToCandidateRatingStep();
		} catch (err) {
			console.error("appendMissingCandidateRatingForms failed:", err);
		} finally {
			this.loadingService.setLoading(false);
		}
	}

	private stripCompetencyObservationsFromRatingForm(): void {
		if (!this.ratingForm?.length) return;

		for (let ci = 0; ci < this.ratingForm.length; ci++) {
			const observations = this.ratingForm.at(ci).get("observations") as UntypedFormArray | null;
			if (!observations) continue;

			for (let mi = observations.length - 1; mi >= 0; mi--) {
				const maneuverCode = (observations.at(mi) as UntypedFormGroup).get("maneuverCode")?.value as string;
				if (isCompetencyManeuverCode(maneuverCode)) {
					observations.removeAt(mi);
				}
			}
		}
	}

	private getMergedManeuverRows(resp: StartRatingResp): Row[] {
		const manueverAll: Row[] = [];

		const manu: Row[] = (resp?.data?.assessmentConfig ?? [])
			.map((m: any) => {
				const mvCode = resolveAssessmentConfigManeuverCode(m);
				const maneuverMeta = m?.maneuver ?? m?.maneuverType ?? {};
				return {
					maneuver: maneuverMeta?.description ?? "",
					maneuverCode: mvCode,
					maneuverSubDescription: maneuverMeta?.subDescription ?? "",
					assessmentId: m?.assessmentId ?? "",
					traineeAssessmentId: resp?.data?.traineeAssessmentId ?? "",
				};
			})
			.filter((r) => r.maneuverCode && !isCompetencyManeuverCode(r.maneuverCode));

		const manuByCode = new Map(manu.map((r) => [r.maneuverCode, r]));
		const allCodes = new Set(manueverAll.map((r) => r.maneuverCode));

		return [
			...manueverAll.map((r) => manuByCode.get(r.maneuverCode) ?? r),
			...manu.filter((r) => !allCodes.has(r.maneuverCode)),
		];
	}

	/**
	 * Populate Step 2 with candidate rating sub-forms and observation groups.
	 * Apply preselected observations and restore any draft values.
	 */
	async startRatingSuccessOffline(resp: StartRatingResp): Promise<void> {
		this.isStep1FormCreated = true;
		this.getCandidateList();

		this.candidateList.forEach((c: string) => {
			this.ratingForm.push(createCandidateRatingForm(this.fb, c, this.assessmentOfflineId));
		});

		const merged = this.getMergedManeuverRows(resp);
		const candidates = resp.data.candidates;
		this.candidateList.forEach((_, i) => {
			const cand = candidates[i];
			if (!cand) return;

			const observations = this.ratingForm.at(i).get("observations") as UntypedFormArray;

			merged.forEach((m: Row) => {
				observations.push(this.addOBs(m.maneuver, m.maneuverCode, m.assessmentId, m.traineeAssessmentId, cand.id));
			});
		});

		this.buildSelectedOb(resp.data);
		this.initCrewRoleIndices();
		refreshManeuverRepeatBaselines(this.ratingForm);
		for (let ci = 0; ci < this.ratingForm.length; ci++) {
			const observations = this.ratingForm.at(ci).get("observations") as UntypedFormArray;
			if (observations) seedAndPersistTs1V3ObUndoBaselines(observations);
		}

		try {
			await this.initRatingValues();
		} catch (error) {
			console.error("initRatingValues failed after Start Rating:", error);
		}

		this.ratingFormLoaded = true;
		this.current = 0;
		this.prev = -1;
		this.normalizeCandidateView();
		this.cd.detectChanges();

		this.goToCandidateRatingStep();
		this.step2V2?.resyncSummary();
		this.step2V2?.refreshObPanelStars();
		this.queueRepeatDisabledSync();
		this.loadingService.setLoading(false);
	}

	/** Step 1 → Candidate Rating (stepper index 1); bypass linear guards after programmatic form setup. */
	private goToCandidateRatingStep(): void {
		const navigate = (): void => {
			if (!this.stepper) return;
			this.forceSelectStep(1);
		};

		if (this.stepper) {
			navigate();
		} else {
			setTimeout(navigate, 0);
		}
	}

	/**
	 * Rehydrate Step 2 values from the draft:
	 * - Add/remove actions and attempt counts.
	 * - Other comments per maneuver.
	 * - Preselected observation list.
	 * - Repeat counter and safety outcome per assessment.
	 *
	 * When appending a new candidate, pass `fromCandidateIndex` so existing rating
	 * forms keep their in-memory OB state instead of being replayed from draft.
	 */
	async initRatingValues(fromCandidateIndex = 0) {
		try {
			if (!this.assessmentOfflineId) return;
			await this.containerRatingsService.loadDraftItem(this.assessmentOfflineId);
			const record = await this.containerRatingsService.getDraftById(this.assessmentOfflineId);
			if (!record) return;

			const candidateRating = record?.candidateRating;
			const candidates = record?.trainingInformation?.candidates ?? [];

			const addRemoveOb = candidateRating?.addRemoveOb ?? [];
			const otherComments = candidateRating?.otherComments ?? [];
			const selectedOBs = candidateRating?.selectedOBs ?? [];
			console.log("selected obs init ratins", selectedOBs);
			const resetManeuvers = candidateRating?.resetManeuvers ?? [];

			if (candidateRating?.crewRoleAssignment) {
				const saved = candidateRating.crewRoleAssignment;
				if (saved.rolesByCandidateIndex?.length >= 2 || saved.pfCandidateIndex != null) {
					this.applyCrewRoleAssignment(saved);
					this.normalizeCrewRoles();
				}
			}
			if (candidateRating?.maneuverOutcomes?.length) {
				this.maneuverOutcomes = [...candidateRating.maneuverOutcomes];
			}
			if (candidateRating?.temCompetencyOutcomes?.length) {
				this.temCompetencyOutcomes = normalizeTemCompetencyOutcomes(
					[...candidateRating.temCompetencyOutcomes],
					Math.max(this.candidateList.length, this.ratingForm?.length ?? 0, 1),
				);
			}
			// Staging strips maneuverOutcomes / temCompetencyOutcomes — rebuild from selectedOBs
			// so Repeat / sidebar / Step 3 still see N/A and ratings after reload.
			this.restoreV3OutcomesFromDraftSelectedOBs(selectedOBs);
			if (record.signOffMode === "remote") {
				this.signOffMode = record.signOffMode;
			}

			for (let formIndex = fromCandidateIndex; formIndex < candidates.length; formIndex++) {
				const candidate = candidates[formIndex];
				if (!candidate) continue;

				const observations = this.ratingForm.at(formIndex)?.get("observations") as UntypedFormArray;
				if (!observations) continue;

				const initialObByKey: Record<string, OBAttempt> = {};
				seedInitialObByKeyFromObservationForms(observations, initialObByKey);
				persistTs1V3ObUndoBaselines(initialObByKey);

				// Reset repeated role columns to session-start baseline before replaying post-repeat events.
				reapplyV3ManeuverRepeatFormState(observations, resetManeuvers, selectedOBs, addRemoveOb);

				for (const a of addRemoveOb) {
					const idx = (observations?.value || []).findIndex(
						(i: any) => i.maneuverCode === a.maneuverCode && i.candidateId === a.candidateId,
					);
					if (idx < 0) continue;

					if (!shouldReplayDraftAddRemoveObEvent(a, resetManeuvers, addRemoveOb, true)) {
						continue;
					}

					const obsGroup = observations.at(idx) as UntypedFormGroup;
					const { cat, num } = parseObsCode(a.observationCode);

					let finalNum = num;
					if (finalNum == null) {
						const maybe = (selectedOBs || []).find(
							(s: any) =>
								s.candidateId === a.candidateId && s.maneuverCode === a.maneuverCode && parseObLabel(s.ob).cat === cat,
						);
						if (maybe) finalNum = parseObLabel(maybe.ob).num;
					}

					if (!cat || finalNum == null) continue;

					const roleSlot = resolveV2ObRoleSlot(a.candidateRole);
					const ctrl = getObCellControl(obsGroup, cat, finalNum!, roleSlot);
					if (!ctrl) continue;

					applyDraftAddRemoveObEvent(ctrl, a, addRemoveOb, initialObByKey);
				}

				applyDisplayObsLastActions(
					observations,
					rebuildDisplaySnapshots(record.candidateRating, collectAssessmentConfigObPresets(this.assessmentConfig))
						.displayObs,
				);

				for (const comms of otherComments) {
					if (!comms?.maneuverCode) continue;
					for (let formIndex = 0; formIndex < candidates.length; formIndex++) {
						const observations = this.ratingForm.at(formIndex)?.get("observations") as UntypedFormArray;
						if (!observations) continue;
						const i = (observations?.value || []).findIndex(
							(row: { maneuverCode?: string }) => row.maneuverCode === comms.maneuverCode,
						);
						if (i >= 0) {
							(observations.at(i) as UntypedFormGroup).get("otherComents")?.patchValue(comms.comment);
						}
					}
				}

				const repeatCount: Record<string, number> = {};
				for (const r of resetManeuvers) {
					const rId = `${r.candidateId}.${r.maneuverCode}`;
					repeatCount[rId] = (repeatCount[rId] ?? 0) + 1;
				}
				for (const key in repeatCount) {
					const [candidateId, maneuverCode] = key.split(".");
					const value = repeatCount[key];
					const i = (observations?.value || []).findIndex(
						(i: any) => i.maneuverCode === maneuverCode && i.candidateId === candidateId,
					);
					console.log("repeat count", value);
					if (i >= 0) (observations.at(i) as UntypedFormGroup).get("repeat")?.patchValue(value);
				}

				const filterSelectedObs: Record<string, any> = {};
				for (const selectedOB of filterSelectedOBsExcludingReset(selectedOBs)) {
					const code = selectedOB.maneuverCode;
					const i = (observations?.value || []).findIndex(
						(i: any) => i.maneuverCode === code && i.candidateId === selectedOB.candidateId,
					);
					if (i >= 0) {
						filterSelectedObs[i] = filterSelectedObs[i] || { obs: [] };
						filterSelectedObs[i].obs.push(selectedOB);
					}
				}
				for (const key in repeatCount) {
					const [candidateId, maneuverCode] = key.split(".");
					const i = (observations?.value || []).findIndex(
						(i: any) => i.maneuverCode === maneuverCode && i.candidateId === candidateId,
					);
					if (i >= 0 && !filterSelectedObs[i]) {
						filterSelectedObs[i] = { obs: [] };
					}
				}
				for (const i in filterSelectedObs) {
					const numI = parseInt(i, 10);
					(observations.at(numI) as UntypedFormGroup).get("selectedOBs")?.patchValue(filterSelectedObs[i].obs);
				}

				for (let mi = 0; mi < observations.length; mi++) {
					const form = observations.at(mi) as UntypedFormGroup;
					const maneuverCode = form.get("maneuverCode")?.value as string;
					if (maneuverCode) {
						applyPreDefinedFlagsFromConfig(form, this.assessmentConfig, maneuverCode);
					}
				}

				applyLatestNaDisabledStateFromAddRemoveOb(observations, addRemoveOb, resetManeuvers, true);
				applyManeuverOutcomeNaToObservationForms(
					observations,
					this.maneuverOutcomes,
					formIndex,
					this.candidateList.length >= 2,
				);
			}
		} catch (error) {
			console.error(error);
		}
	}

	/**
	 * Staging omits draft-only outcome fields. Merge restored values onto any maneuver/TEM
	 * shells that still lack activity so Repeat / sidebar / Step 3 work after reload.
	 */
	private restoreV3OutcomesFromDraftSelectedOBs(selectedOBs: OBComments[]): void {
		if (!selectedOBs?.length) return;

		const restored = restoreV3OutcomesFromSelectedOBs(selectedOBs);
		if (!restored.maneuverOutcomes.length && !restored.temCompetencyOutcomes.length) return;

		let changed = false;
		const byCode = new Map(this.maneuverOutcomes.map((o) => [o.maneuverCode, o]));
		for (const outcome of restored.maneuverOutcomes) {
			const existing = byCode.get(outcome.maneuverCode);
			if (existing && maneuverOutcomeHasActivity(existing)) continue;
			byCode.set(outcome.maneuverCode, { ...(existing ?? { maneuverCode: outcome.maneuverCode }), ...outcome });
			changed = true;
		}
		if (changed) {
			this.maneuverOutcomes = Array.from(byCode.values());
		}

		const hasTemActivity = this.temCompetencyOutcomes.some((o) =>
			(["pfPrimary", "pm", "pmPrimary", "pf"] as const).some((slot) => o[slot] != null),
		);
		if (!hasTemActivity && restored.temCompetencyOutcomes.length) {
			this.temCompetencyOutcomes = normalizeTemCompetencyOutcomes(
				restored.temCompetencyOutcomes,
				Math.max(this.candidateList.length, this.ratingForm?.length ?? 0, 1),
			);
			changed = true;
		}

		if (!changed) return;

		const cr = getSafeCandidateRating(this.containerRatingsService.draft);
		this.containerRatingsService.updateSelectedDraft({
			candidateRating: {
				...cr,
				maneuverOutcomes: this.maneuverOutcomes,
				temCompetencyOutcomes: this.temCompetencyOutcomes,
			},
			lastUpdatedDate: new Date().toISOString(),
		});
	}

	buildSelectedOb(data: StartRatingRespData, fromCandidateIndex = 0): void {
		const configs = data.assessmentConfig || [];

		if (fromCandidateIndex === 0) {
			this.initCrewRoleIndices();
		}

		for (const cfg of configs) {
			const mvCode = resolveAssessmentConfigManeuverCode(cfg);
			if (!mvCode || isCompetencyManeuverCode(mvCode)) continue;
			const candidateCount = this.ratingForm?.length ?? this.candidateList.length;

			for (const x of cfg.observations ?? []) {
				const attempt = Number(x?.attempt ?? 0);
				if (attempt <= 0) continue;

				const observationCode = resolveObservationConfigCode(x);
				if (!observationCode) continue;

				const roles = this.resolvePreselectionRoles(x?.candidateRole);
				for (const role of roles) {
					for (let ci = fromCandidateIndex; ci < candidateCount; ci++) {
						this.patchPreselectedOb(ci, mvCode, observationCode, attempt, role);
					}
				}
			}
		}

		if (configs.length) {
			const codes = configs
				.map((c) => resolveAssessmentConfigManeuverCode(c))
				.filter((code): code is string => !!code && !isCompetencyManeuverCode(code));
			if (fromCandidateIndex === 0) {
				this.maneuverOutcomes = codes.map((code) => ({ maneuverCode: code }));
			} else {
				const existing = new Set(this.maneuverOutcomes.map((o) => o.maneuverCode));
				for (const code of codes) {
					if (!existing.has(code)) {
						this.maneuverOutcomes.push({ maneuverCode: code });
					}
				}
			}
		}

		this.ts1RatingForm?.markAllAsTouched();
		this.cd.detectChanges();
	}

	private patchPreselectedOb(
		candidateIndex: number,
		mvCode: string,
		code: string,
		attempt: number,
		role?: CandidateRole,
	): void {
		const obsFA = this.ratingForm.at(candidateIndex).get("observations") as UntypedFormArray;
		if (!obsFA) return;

		const idx = obsFA.controls.findIndex((fg) => (fg as UntypedFormGroup).get("maneuverCode")!.value === mvCode);
		if (idx === -1) return;

		const row = obsFA.at(idx) as UntypedFormGroup;
		this.patchObControl(row, code, attempt, mvCode, role);
	}

	override getObControl(
		obsGroup: UntypedFormGroup,
		cat: string,
		num: number,
		role?: CandidateRole,
	): UntypedFormControl | null {
		return getObCellControl(obsGroup, cat, num, resolveV2ObRoleSlot(role));
	}

	private patchObControl(
		row: UntypedFormGroup,
		code: string,
		attempt: number,
		mvCode: string,
		role?: CandidateRole,
	): void {
		const [catLower, numStr] = (code || "").split("_");
		const cat = (catLower || "").toUpperCase();
		const num = Number(numStr);
		const ctrl = getObCellControl(row, cat, num, resolveV2ObRoleSlot(role));
		if (!ctrl) return;

		ctrl.patchValue(
			{
				totalCount: attempt,
				currentCount: attempt,
				preDefined: attempt > 0,
				lastAction: attempt > 0 ? CONSTANT.OB_LAST_ACTION.PRE_SELECTED : CONSTANT.OB_LAST_ACTION.NO_ACTION,
				fixedManeuverCode: mvCode,
				isDisabled: false,
			},
			{ emitEvent: false },
		);
	}

	initCrewRoleIndices(): void {
		const saved = this.containerRatingsService.draft?.candidateRating?.crewRoleAssignment;

		if (saved?.rolesByCandidateIndex && saved.rolesByCandidateIndex.length >= 2) {
			this.applyCrewRoleAssignment(saved);
		} else {
			this.resetCrewRolesToDefault();
		}

		this.normalizeCrewRoles();
	}

	private resetCrewRolesToDefault(): void {
		this.candidateCrewRoles = [...Ts1RatingFormV3Component.DEFAULT_CREW_ROLES];
	}

	private applyCrewRoleAssignment(saved: CrewRoleAssignment): void {
		const count = Math.max(this.ratingForm?.length ?? 0, this.candidates?.length ?? 0, this.candidateList?.length ?? 0);

		if (saved.rolesByCandidateIndex?.length) {
			this.candidateCrewRoles = saved.rolesByCandidateIndex.slice(0, Math.max(count, 2));
			while (this.candidateCrewRoles.length < 2) {
				this.candidateCrewRoles.push(
					this.candidateCrewRoles[0] === CandidateRole.PF ? CandidateRole.PM : CandidateRole.PF,
				);
			}
		} else if (saved.pfCandidateIndex != null && saved.pmCandidateIndex != null) {
			// Legacy drafts: map old index-based assignment to fixed slots (0 = PF, 1 = PM by default).
			this.candidateCrewRoles = [...Ts1RatingFormV3Component.DEFAULT_CREW_ROLES];
			if (saved.pfCandidateIndex === 1 && saved.pmCandidateIndex === 0) {
				this.candidateCrewRoles = [CandidateRole.PM, CandidateRole.PF];
			}
		}
	}

	/** Slot 0 / slot 1 always carry PF and PM for the dual-column UI (even with one candidate). */
	private normalizeCrewRoles(): void {
		if (this.candidateCrewRoles.length < 2) {
			this.resetCrewRolesToDefault();
			return;
		}

		const hasPf = this.candidateCrewRoles.includes(CandidateRole.PF);
		const hasPm = this.candidateCrewRoles.includes(CandidateRole.PM);
		if (!hasPf || !hasPm || this.candidateCrewRoles[0] === this.candidateCrewRoles[1]) {
			this.resetCrewRolesToDefault();
		}
	}

	private normalizeCandidateRole(role: string | undefined | null): CandidateRole | null {
		return normalizeConfigCandidateRole(role);
	}

	private resolvePreselectionRoles(role: string | undefined | null): CandidateRole[] {
		const resolved = normalizeConfigCandidateRole(role);
		if (resolved) return [resolved];
		return [CandidateRole.PF, CandidateRole.PM];
	}

	swapCrewRoles(): void {
		const candidateCount = Math.max(this.candidateList.length, this.ratingForm?.length ?? 0);
		if (candidateCount < 2) return;

		const roles = [...this.candidateCrewRoles];
		if (roles.length >= 2) {
			const tmp = roles[0];
			roles[0] = roles[1];
			roles[1] = tmp;
			this.candidateCrewRoles = roles;
		}

		this.persistCrewRoleAssignment();
		this.temCompetencyOutcomes = normalizeTemCompetencyOutcomes(this.temCompetencyOutcomes, candidateCount);
		this.invalidateStep3ValidationCache();
		this.cd.detectChanges();
		this.queueRepeatDisabledSync();
	}

	onRepeatManeuver(): void {
		this.step2V2?.onRepeatClick();
		this.queueRepeatDisabledSync();
	}

	/**
	 * Defer reading step2/OB ViewChild state until after the current CD turn.
	 * Binding a live getter through unresolved ViewChildren causes NG0100 (true → false).
	 */
	queueRepeatDisabledSync(): void {
		if (this.repeatDisabledSyncQueued) {
			return;
		}
		this.repeatDisabledSyncQueued = true;
		queueMicrotask(() => {
			this.repeatDisabledSyncQueued = false;
			this.syncRepeatDisabled();
			// Step 2 mounts behind @if — retry once after paint if ViewChild was still missing.
			if (this.repeatDisabled && !this.step2V2 && this.stepper?.selectedIndex === 1) {
				setTimeout(() => this.syncRepeatDisabled(), 0);
			}
		});
	}

	private syncRepeatDisabled(): void {
		const next = this.step2V2?.repeatDisabled ?? true;
		if (this.repeatDisabled === next) {
			return;
		}
		this.repeatDisabled = next;
		this.cd.markForCheck();
	}

	onManeuverOutcomesChange(outcomes: ManeuverOutcomeDraft[]): void {
		const previousList = this.maneuverOutcomes;
		let next = [...outcomes];

		if (this.continuePage >= 3 && this.ratingForm?.length) {
			for (const outcome of outcomes) {
				const previous = previousList.find((row) => row.maneuverCode === outcome.maneuverCode);
				const synced = syncManeuverOutcomeNaCascade({
					previous,
					outcome,
					containerRatingsService: this.containerRatingsService,
					formForSlot: (slot) => findManeuverFormForOutcomeSlot(this.ratingForm, slot, outcome.maneuverCode),
				});
				const idx = next.findIndex((row) => row.maneuverCode === synced.maneuverCode);
				if (idx >= 0) next[idx] = synced;
				else next.push(synced);
			}

			const cr = getSafeCandidateRating(this.containerRatingsService.draft);
			const ids = this.reviewCandidateIds;
			let selectedOBs = cr.selectedOBs ?? [];
			const hasSecondCandidate = ids.length >= 2;
			for (const outcome of next) {
				if (!outcome.maneuverCode) continue;
				const label =
					this.assessmentConfig.find((item) => item.maneuverType.code === outcome.maneuverCode)?.maneuverType
						.description ?? outcome.maneuverCode;
				selectedOBs = ensureManeuverOutcomeHistoryInSelectedOBs(
					selectedOBs,
					next,
					outcome.maneuverCode,
					label,
					{ pf: ids[0] || ids[1] || "", pm: ids[1] || ids[0] || "" },
					hasSecondCandidate,
				);
			}

			this.containerRatingsService.updateSelectedDraft({
				candidateRating: { ...cr, maneuverOutcomes: next, selectedOBs },
				lastUpdatedDate: new Date().toISOString(),
				continuePage: Math.max(this.continuePage, 3),
			});
		} else if (this.continuePage >= 3) {
			const cr = getSafeCandidateRating(this.containerRatingsService.draft);
			this.containerRatingsService.updateSelectedDraft({
				candidateRating: { ...cr, maneuverOutcomes: next },
				lastUpdatedDate: new Date().toISOString(),
				continuePage: Math.max(this.continuePage, 3),
			});
		}

		this.maneuverOutcomes = next;
		this.invalidateReviewCaches();
		this.syncStep3ScrollHighlightIfResolved();
		this.queueRepeatDisabledSync();
		this.cd.markForCheck();
	}

	onTemCompetencyOutcomesChange(outcomes: TemCompetencyOutcomeDraft[]): void {
		this.temCompetencyOutcomes = normalizeTemCompetencyOutcomes(
			outcomes,
			Math.max(this.candidateList.length, this.ratingForm?.length ?? 0, 1),
		);
		this.invalidateReviewCaches();
		this.syncStep3ScrollHighlightIfResolved();
		this.cd.markForCheck();
	}

	onReviewOutcomeComment(comment: OBComments): void {
		const cr = getSafeCandidateRating(this.containerRatingsService.draft);
		let maneuverOutcomes = this.maneuverOutcomes;
		let temCompetencyOutcomes = this.temCompetencyOutcomes;

		if (comment.isTemCompetencyOutcome) {
			temCompetencyOutcomes = applyTemCompetencySummaryCommentToDraft(this.temCompetencyOutcomes, comment);
			this.temCompetencyOutcomes = temCompetencyOutcomes;
		} else {
			maneuverOutcomes = applyOutcomeSummaryCommentToDraft(this.maneuverOutcomes, comment);
			this.maneuverOutcomes = maneuverOutcomes;
		}

		const selectedOBs = appendOutcomeToSelectedOBs(cr.selectedOBs ?? [], comment);

		this.containerRatingsService.updateSelectedDraft({
			candidateRating: { ...cr, maneuverOutcomes, temCompetencyOutcomes, selectedOBs },
			lastUpdatedDate: new Date().toISOString(),
			continuePage: Math.max(this.continuePage, 3),
		});
		this.invalidateReviewCaches();
		this.queueRepeatDisabledSync();
		this.cd.markForCheck();
	}

	onReviewRecommendationChanged(): void {
		this.invalidateStep3ValidationCache();
		this.syncStep3ScrollHighlightIfResolved();
		this.syncSignOffFormToDraftMemory();
		this.queueSignOffDraftSave();
		this.cd.markForCheck();
	}

	onReviewSummaryEdited(): void {
		this.invalidateReviewCaches();
		this.cd.markForCheck();
	}

	private queueSignOffDraftSave(): void {
		if (this.signOffDraftSaveTimer) {
			clearTimeout(this.signOffDraftSaveTimer);
		}
		this.signOffDraftSaveTimer = setTimeout(() => {
			this.signOffDraftSaveTimer = null;
			void this.saveSignOffDraft();
		}, Ts1RatingFormV3Component.SIGN_OFF_DRAFT_DEBOUNCE_MS);
	}

	private reviewSummaryCommentsCache: { index: number; key: string; comments: OBComments[] } | null = null;

	invalidateReviewSummaryCache(): void {
		this.reviewSummaryCommentsCache = null;
	}

	/** Drop rating/summary caches used by Step 3 Eager CD getters. */
	private invalidateReviewCaches(): void {
		this.reviewSummaryCommentsCache = null;
		this.reviewPreviewSummaryCache = null;
		this.reviewCompetencyBlocksCache.clear();
		this.invalidateStep3ValidationCache();
	}

	private invalidateStep3ValidationCache(): void {
		this.step3CoreCompleteCache = null;
		this.step3IncompleteReasonsCache = null;
		this.step3IncompleteItemsCache = null;
		this.step2ManeuverCompleteCache = null;
		this.step2IncompleteReasonsCache = null;
	}

	/** Refresh Step 2 UI only while that step is mounted. */
	private refreshStep2IfActive(): void {
		if (this.stepper?.selectedIndex !== 1) return;
		this.step2V2?.resyncSummary();
		this.step2V2?.refreshObPanelStars();
		this.queueRepeatDisabledSync();
	}

	private reviewSummaryCacheKey(candidateIndex: number): string {
		const cr = getSafeCandidateRating(this.containerRatingsService.draft);
		return [
			candidateIndex,
			cr.selectedOBs?.length ?? 0,
			cr.addRemoveOb?.length ?? 0,
			cr.resetManeuvers?.length ?? 0,
			this.maneuverOutcomes.length,
			this.current,
		].join(":");
	}

	private persistCrewRoleAssignment(): void {
		const cr = getSafeCandidateRating(this.containerRatingsService.draft);
		this.containerRatingsService.updateSelectedDraft({
			candidateRating: {
				...cr,
				crewRoleAssignment: {
					rolesByCandidateIndex: [...this.candidateCrewRoles],
					pfCandidateIndex: this.pfCandidateIndex,
					pmCandidateIndex: this.pmCandidateIndex,
				},
			},
			lastUpdatedDate: new Date().toISOString(),
			continuePage: 2,
		});
	}

	/**
	 * Create a new observation group for a candidate and maneuver.
	 */
	addOBs(
		maneuver: string,
		maneuverCode: string,
		assessmentId: string,
		traineeAssessmentId: string,
		candidateId: string,
	): UntypedFormGroup {
		const tmpl = this.observationData?.[0] ?? {};
		const categoryTemplate = Object.fromEntries(
			Object.entries(tmpl).filter(([k, v]) => typeof v === "object" && v && k !== "maneuver"),
		);

		const filtered: any = {};
		for (const cat in categoryTemplate) {
			const group = (categoryTemplate as any)[cat];
			if (!group || typeof group !== "object") continue;

			for (const key in group) {
				const ob = group[key];

				(filtered[cat] ||= {})[key] = ob; // keep only matching code
			}
		}

		const sections = buildSectionsFromTemplateV2(this.fb, filtered, maneuverCode);

		return this.fb.group({
			maneuver: [maneuver, []],
			maneuverCode: [maneuverCode, [Validators.required]],
			assessmentId: [assessmentId, []],
			traineeAssessmentId: [traineeAssessmentId, [Validators.required]],
			candidateId: [candidateId, [Validators.required]],
			...sections,
			safetyOutcome: [-2, []],
			repeat: [0, []],
			otherComents: ["", []],
			selectedOBs: [[], []],
		});
	}

	/**
	 * Ensure sign-off subforms match the current candidate set.
	 * If the set changed, clear and rebuild to maintain alignment.
	 */
	private rebuildTs1SignOffFormsIfNeeded(): void {
		this.ensureAssessmentOfflineId();

		if (!this.ts1SignOffForm) {
			this.ts1SignOffForm = createSignOffFormContainer(this.fb);
		}

		const candidates = (this.getTrainingInformation()?.candidates ?? []).sort((a, b) =>
			a.isPrimary === b.isPrimary ? 0 : a.isPrimary ? -1 : 1,
		);

		// const existingIds = (this.signOffForm?.controls ?? [])
		//   .map((fg) => fg.get("candidateId")?.value)
		//   .filter((v) => !!v);

		// const candidateIds = candidates.map((c) => c.candidateId).filter((v) => !!v);

		// const needRebuild =
		//   existingIds.length !== candidateIds.length ||
		//   candidateIds.some((id) => !existingIds.includes(id));

		// if (!needRebuild) return;

		const liveByCandidateId = new Map<string, { control: UntypedFormGroup; raw: any }>();
		const liveByIndex: any[] = [];
		for (const control of this.signOffForm.controls) {
			const fg = control as UntypedFormGroup;
			const raw = fg.getRawValue?.() ?? fg.value;
			liveByIndex.push(raw);
			if (raw?.candidateId) {
				liveByCandidateId.set(raw.candidateId, { control: fg, raw });
			}
		}
		const liveInstructor = liveByIndex[0];

		while (this.signOffForm.length) {
			this.signOffForm.removeAt(this.signOffForm.length - 1, { emitEvent: false });
		}

		const signOffDetails = this.getSignOffDetailsData();
		const candidateSignOffDetails = signOffDetails?.candidateSignOffDetails ?? {};
		const trainerName = this.roleService.userDetails?.name ?? "";

		this.getCandidateList();

		candidates.forEach((c, index) => {
			if (!c.candidateId || !this.assessmentOfflineId) return;

			const saved = candidateSignOffDetails[c.candidateId];
			const liveEntry = liveByCandidateId.get(c.candidateId);
			const live = liveEntry?.raw;
			const liveControl = liveEntry?.control;
			const generalObservation = liveControl
				? signOffNoteForAutosave(liveControl.get("generalObservation"), saved?.generalObservation)
				: saved?.generalObservation;
			const reminderNotes = liveControl
				? signOffNoteForAutosave(liveControl.get("reminderNotes"), saved?.reminderNotes)
				: saved?.reminderNotes;
			const details = {
				...saved,
				...live,
				finalRating: live?.finalRating ?? saved?.finalRating,
				category: live?.category || saved?.category,
				sessionCheck: live ? live.sessionCheck : saved?.sessionCheck,
				objectiveCheck: live ? live.objectiveCheck : saved?.objectiveCheck,
				clearanceCheck: live ? live.clearanceCheck : saved?.clearanceCheck,
				useInstructorLicense:
					liveInstructor?.useInstructorLicense ?? live?.useInstructorLicense ?? saved?.useInstructorLicense,
				instructorLicenseNumber:
					liveInstructor?.instructorLicenseNumber || live?.instructorLicenseNumber || saved?.instructorLicenseNumber,
				trainerName: liveInstructor?.trainerName || live?.trainerName || saved?.trainerName,
				trainerSignature: liveInstructor?.trainerSignature ?? live?.trainerSignature ?? saved?.trainerSignature ?? null,
				traineeSignature: live?.traineeSignature ?? saved?.traineeSignature ?? null,
				generalObservation,
				reminderNotes,
				reminderNoteResponses: live?.reminderNoteResponses?.length
					? live.reminderNoteResponses
					: saved?.reminderNoteResponses,
			};

			const result = this.offlineRatingService.calculate(c.candidateId);

			const rating = {
				ratingA: result?.ratingA ?? 1,
				ratingB: result?.ratingB ?? 1,
				ratingC: result?.ratingC ?? 1,
			};

			// Determine if using instructor license or examiner number
			const useInstructorLicense =
				details?.useInstructorLicense !== undefined ? coerceBool(details?.useInstructorLicense, true) : true;

			// Get license number from draft, or profile service as fallback
			let instructorLicenseNumber = details?.instructorLicenseNumber;
			if (
				!instructorLicenseNumber ||
				(typeof instructorLicenseNumber === "string" && instructorLicenseNumber.trim() === "")
			) {
				// Try to get from profile service based on useInstructorLicense flag
				if (useInstructorLicense) {
					instructorLicenseNumber = this.profileService?.profile?.pilot_license_number ?? "";
				} else {
					instructorLicenseNumber = this.profileService?.profile?.examiner_number ?? "";
				}
			}

			const signOffData = {
				ratingA: rating?.ratingA,
				ratingB: rating?.ratingB,
				ratingC: rating?.ratingC,
				finalRating: details?.finalRating,
				category: details?.category === "Check" ? "Check" : "Training",
				sessionCheck: details?.sessionCheck,
				objectiveCheck: details?.objectiveCheck,
				clearanceCheck: details?.clearanceCheck,
				useInstructorLicense,
				instructorLicenseNumber: instructorLicenseNumber ?? "",
				trainerName: details?.trainerName ?? trainerName,
				traineeName: this.candidateList[index] ?? details?.traineeName,
				trainerSignature: details?.trainerSignature ?? null,
				traineeSignature: details?.traineeSignature ?? null,
				generalObservation: details?.generalObservation ?? "",
				reminderNotes:
					details?.reminderNotes ??
					(this.rawFormData?.reminderNotesEnabled ? (this.rawFormData?.reminderNotesTemplate ?? "") : ""),
			};

			const form = createCandidateSignoffForm(CONSTANT.FORM_TYPE.ts_1, this.fb, {
				candidateName: this.candidateList[index],
				candidateId: c.candidateId,
				traineeAssessmentId: this.assessmentOfflineId!,
				trainerName,
				signOffDetails: signOffData,
			});

			if (this.useEts1AocSessions && (this.rawFormData?.ets1SessionVersion ?? 1) === 2) {
				const savedByNoteId = new Map<
					string | undefined,
					{ noteId?: string; text?: string | null; status?: string | null }
				>(
					(details?.reminderNoteResponses ?? []).map(
						(note: { noteId?: string; text?: string | null; status?: string | null }) => [note.noteId, note] as const,
					),
				);
				form.setControl(
					"reminderNoteResponses",
					this.fb.array(
						(this.rawFormData?.reminderNotes ?? []).map(
							(note: { id?: string; text?: string; requireStatus?: boolean }) => {
								const saved = savedByNoteId.get(note.id);
								const savedText = String(saved?.text ?? "").trim();
								return this.fb.group({
									noteId: [note.id ?? ""],
									text: [savedText || note.text || ""],
									requireStatus: [!!note.requireStatus],
									status: [saved?.status ?? null, note.requireStatus ? [Validators.required] : []],
								});
							},
						),
					),
				);
			}

			this.signOffForm.push(form, { emitEvent: false });
		});

		this.signOffForm.updateValueAndValidity({ emitEvent: false });
		this.ts1SignOffForm.updateValueAndValidity({ emitEvent: false });

		this.cd.detectChanges();
	}

	/**
	 * Step 2 → Step 3 (Review) transition.
	 */
	onStepperNextReview(_stepper: MatStepper, bypassValidation: boolean = false): void {
		if (!bypassValidation) {
			if (this.ts1RatingForm.invalid) {
				this.ts1RatingForm.markAllAsTouched();
				return;
			}
			void this.saveAsDraft(Math.max(this.continuePage, 3));
		}

		this.invalidateReviewCaches();
		this.getCandidateList();
		this.rebuildTs1SignOffFormsIfNeeded();
		this.current = 0;
		this.prev = -1;
		this.continuePage = Math.max(this.continuePage, 3);

		if (bypassValidation) this.forceSelectStep(2);
		else this.stepper.next();
	}

	get reviewCandidateIds(): string[] {
		const count = Math.max(
			this.ratingForm?.length ?? 0,
			this.candidateList.length,
			this.containerRatingsService?.draft?.trainingInformation?.candidates?.length ?? 0,
		);
		if (!count) return [];
		return Array.from({ length: count }, (_, i) => this.resolveReviewCandidateId(i));
	}

	private candidateIdFromRatingFormIndex(index: number): string {
		if (!this.ratingForm?.length || index < 0 || index >= this.ratingForm.length) return "";
		const obs = this.ratingForm.at(index).get("observations") as UntypedFormArray | null;
		if (!obs?.length) return "";

		for (let mi = 0; mi < obs.length; mi++) {
			const id = (obs.at(mi) as UntypedFormGroup | undefined)?.get("candidateId")?.value ?? "";
			if (id) return id;
		}
		return "";
	}

	private resolveReviewCandidateId(index: number): string {
		const fromForm = this.candidateIdFromRatingFormIndex(index);
		if (fromForm) return fromForm;

		const draftCandidates = this.containerRatingsService?.draft?.trainingInformation?.candidates ?? [];
		return (draftCandidates[index] as { candidateId?: string } | undefined)?.candidateId ?? "";
	}

	buildReviewCompetencyBlocksForIndex(candidateIndex: number): Ts1ReviewCompetencyBlock[] {
		if (!this.ratingFormLoaded || !this.ratingForm?.length) {
			return TS1_REVIEW_COMPETENCY_ORDER.map((code) => ({
				code,
				ratingA: null,
				ratingB: null,
				ratingC: null,
				finalScore: 0,
				notGraded: true,
			}));
		}

		const cached = this.reviewCompetencyBlocksCache.get(candidateIndex);
		if (cached) return cached;

		const previews = this.getCachedReviewPreviewSummaryData();
		const candidateIds = this.reviewCandidateIds;
		const currentCandidateId = candidateIds[candidateIndex];
		const preview = previews.find((p) => p.id === currentCandidateId) ?? previews[candidateIndex];

		const labels: string[] = preview?.radarChartLabels ?? [];
		const howManyData: number[] = preview?.radarChartDatasets?.[0]?.data ?? [];
		const howOftenData: number[] = preview?.radarChartDatasets?.[1]?.data ?? [];

		const howManyByCompetency = new Map<string, number>();
		const howOftenByCompetency = new Map<string, number>();
		labels.forEach((code, idx) => {
			howManyByCompetency.set(code, howManyData[idx] ?? 0);
			howOftenByCompetency.set(code, howOftenData[idx] ?? 0);
		});

		const formApplicable = buildTs1CompetencyApplicableCounts({
			ratingForm: this.ratingForm,
			candidateIndex,
		});

		const draftAddRemoveOb = this.containerRatingsService?.draft?.candidateRating?.addRemoveOb ?? [];
		const candidateDraftOb = applyManeuverOutcomeNaDisabledToEvents(
			draftAddRemoveOb.filter((row) => row.candidateId === currentCandidateId),
			this.maneuverOutcomes,
			candidateIndex,
			this.candidateList.length >= 2,
		);
		const obApplicable = buildTs1ApplicableCountsFromAddRemoveOb(mergeTs1V3ObEventsForRatingCalc(candidateDraftOb));

		// Match checklist preview: graded when form, draft OB history, or preview calc has the competency.
		const applicableByCompetency = new Map<string, number>(
			TS1_REVIEW_COMPETENCY_ORDER.map((code) => [
				code,
				(formApplicable.get(code) ?? 0) > 0 || (obApplicable.get(code) ?? 0) > 0 || labels.includes(code) ? 1 : 0,
			]),
		);

		const blocks = buildTs1ReviewCompetencyBlocks({
			howManyByCompetency,
			howOftenByCompetency,
			applicableByCompetency,
			temCompetencyOutcomes: this.temCompetencyOutcomes,
			candidateIndex,
			hasSecondCandidate: this.candidateList.length >= 2,
		});
		this.reviewCompetencyBlocksCache.set(candidateIndex, blocks);
		return blocks;
	}

	private getCachedReviewPreviewSummaryData(): any[] {
		if (this.reviewPreviewSummaryCache) {
			return this.reviewPreviewSummaryCache;
		}
		this.reviewPreviewSummaryCache = this.getPreviewSummaryData();
		return this.reviewPreviewSummaryCache;
	}

	get reviewCompetencyBlocks(): Ts1ReviewCompetencyBlock[] {
		return this.buildReviewCompetencyBlocksForIndex(this.current);
	}

	buildReviewManeuverRowsForIndex(candidateIndex: number): Ts1ReviewManeuverRow[] {
		return buildTs1ReviewManeuverRows({
			assessmentConfig: this.assessmentConfig,
			maneuverOutcomes: this.maneuverOutcomes,
			currentIndex: candidateIndex,
			hasSecondCandidate: this.candidateList.length >= 2,
		});
	}

	buildReviewSummaryCommentsForIndex(candidateIndex: number): OBComments[] {
		const candidateId = this.resolveReviewCandidateId(candidateIndex);
		if (!candidateId || !this.ratingForm?.length) return [];

		const cacheKey = this.reviewSummaryCacheKey(candidateIndex);
		if (this.reviewSummaryCommentsCache?.index === candidateIndex && this.reviewSummaryCommentsCache.key === cacheKey) {
			return this.reviewSummaryCommentsCache.comments;
		}

		const cr = getSafeCandidateRating(this.containerRatingsService.draft);
		const comments = buildTs1V3CandidateSummaryComments({
			ratingForm: this.ratingForm,
			draftSelected: cr.selectedOBs ?? [],
			crewRoles: this.candidateCrewRoles,
			addRemoveOb: cr.addRemoveOb ?? [],
			resetManeuvers: cr.resetManeuvers ?? [],
			maneuverOutcomes: this.maneuverOutcomes,
			candidateId,
		});
		this.reviewSummaryCommentsCache = { index: candidateIndex, key: cacheKey, comments };
		return comments;
	}

	/** Competency OB comments for Preview Summary — sourced from live form + draft selectedOBs. */
	buildPreviewCompetencyObCommentsForIndex(candidateIndex: number): OBComments[] {
		if (!this.ratingForm?.length) return [];

		const candidateId = this.resolveReviewCandidateId(candidateIndex);
		if (!candidateId) return [];

		const cr = getSafeCandidateRating(this.containerRatingsService.draft);
		const merged = mergeTs1V3SelectedOBsForSummary(this.ratingForm, cr.selectedOBs ?? []);

		return merged.filter((row) => {
			if (row.isRepeat || row.isReset) return false;
			if (row.candidateId && row.candidateId !== candidateId) return false;
			return true;
		});
	}

	getReviewTraineeAssessmentIdForIndex(candidateIndex: number): string {
		const obs = this.ratingForm?.at(candidateIndex)?.get("observations") as UntypedFormArray | null;
		return (obs?.at(0) as UntypedFormGroup | undefined)?.get("traineeAssessmentId")?.value ?? "";
	}

	getSignOffControlForIndex(candidateIndex: number): UntypedFormGroup | null {
		const ctrl = this.signOffForm?.at(candidateIndex) as UntypedFormGroup | undefined;
		return ctrl ?? null;
	}

	readonly previewCompetencyBlocksForIndex = (index: number) => this.buildReviewCompetencyBlocksForIndex(index);
	readonly previewManeuverRowsForIndex = (index: number) => this.buildReviewManeuverRowsForIndex(index);
	readonly previewSummaryCommentsForIndex = (index: number) => this.buildReviewSummaryCommentsForIndex(index);
	readonly previewCompetencyObCommentsForIndex = (index: number) =>
		this.buildPreviewCompetencyObCommentsForIndex(index);
	readonly previewTraineeAssessmentIdForIndex = (index: number) => this.getReviewTraineeAssessmentIdForIndex(index);
	readonly previewCandidateDetailsForIndex = (index: number) => this.buildPreviewCandidateDetailsForIndex(index);

	private buildPreviewCandidateDetailsForIndex(index: number) {
		const ctrl = this.candidates.at(index) as UntypedFormGroup | undefined;
		if (!ctrl) return null;

		return buildTs1PreviewCandidateDetails(ctrl, this.sessionKeys, this.syllabusStartDate.value);
	}

	private step2ManeuverValidationParams() {
		return {
			candidateCount: Math.max(this.candidateList.length, this.ratingForm?.length ?? 0),
			crewRoles: this.candidateCrewRoles,
			assessmentConfig: this.assessmentConfig,
			maneuverOutcomes: this.maneuverOutcomes,
		};
	}

	get isStep2ManeuverOutcomesComplete(): boolean {
		if (this.step2ManeuverCompleteCache != null) return this.step2ManeuverCompleteCache;
		this.step2ManeuverCompleteCache = isStep2ManeuverOutcomesComplete(this.step2ManeuverValidationParams());
		return this.step2ManeuverCompleteCache;
	}

	/** Inline hints for incomplete maneuver outcomes blocking Step 2 → Review. */
	get step2ManeuverOutcomeIncompleteReasons(): string[] {
		if (this.step2IncompleteReasonsCache) return this.step2IncompleteReasonsCache;
		this.step2IncompleteReasonsCache = getStep2ManeuverOutcomeIncompleteReasons(this.step2ManeuverValidationParams());
		return this.step2IncompleteReasonsCache;
	}

	private step3ReviewValidationParams() {
		const cr = getSafeCandidateRating(this.containerRatingsService.draft);
		return {
			// Match Step 2: ratingForm can briefly lead candidateList before getCandidateList() syncs.
			candidateCount: Math.max(this.candidateList.length, this.ratingForm?.length ?? 0),
			crewRoles: this.candidateCrewRoles,
			assessmentConfig: this.assessmentConfig,
			maneuverOutcomes: this.maneuverOutcomes,
			temCompetencyOutcomes: this.temCompetencyOutcomes,
			signOffForm: this.signOffForm,
			getCompetencyBlocksForIndex: (index: number) => this.buildReviewCompetencyBlocksForIndex(index),
			ratingForm: this.ratingForm,
			getCandidateNameForIndex: (index: number) => this.candidateList[index] ?? `Candidate ${index + 1}`,
			getCandidateIdForIndex: (index: number) => this.resolveReviewCandidateId(index),
			selectedOBs: cr.selectedOBs ?? [],
			addRemoveOb: cr.addRemoveOb ?? [],
			resetManeuvers: cr.resetManeuvers ?? [],
		};
	}

	get isStep3CoreReviewComplete(): boolean {
		if (this.step3CoreCompleteCache != null) return this.step3CoreCompleteCache;
		this.step3CoreCompleteCache = isStep3CoreReviewComplete(this.step3ReviewValidationParams());
		return this.step3CoreCompleteCache;
	}

	get isStep3ReviewComplete(): boolean {
		return isStep3ReviewComplete(this.step3ReviewValidationParams());
	}

	/** Inline hints for ratings/recommendation gaps only — OB comment gaps use the Sign-off popup. */
	get step3ReviewIncompleteReasons(): string[] {
		return this.step3ReviewIncompleteItems.map((item) => item.reason);
	}

	get step3ReviewIncompleteItems(): Step3ReviewIncompleteItem[] {
		if (this.step3IncompleteItemsCache) return this.step3IncompleteItemsCache;
		this.step3IncompleteItemsCache = getStep3CoreReviewIncompleteItems(this.step3ReviewValidationParams());
		return this.step3IncompleteItemsCache;
	}

	get step3ReviewIncompleteGroups(): Step3ReviewIncompleteGroup[] {
		return groupStep3CoreReviewIncompleteItems(this.step3ReviewIncompleteItems);
	}

	get step3ScrollHighlightKeyForCurrentCandidate(): string | null {
		if (!this.step3ScrollHighlight || this.step3ScrollHighlight.candidateIndex !== this.current) {
			return null;
		}
		return this.step3ScrollHighlight.key;
	}

	toggleStep3GapsChecklist(): void {
		this.step3GapsChecklistExpanded = !this.step3GapsChecklistExpanded;
		this.cd.markForCheck();
	}

	goToStep3IncompleteItem(item: Step3ReviewIncompleteItem, event?: Event): void {
		const tapped = event?.currentTarget;
		if (tapped instanceof HTMLElement) {
			tapped.blur();
		}

		if (item.candidateIndex >= 0 && this.current !== item.candidateIndex) {
			this.current = item.candidateIndex;
		}

		this.pinStep3ScrollHighlightFromChecklist(item);
		this.step3Review?.prepareScrollTarget(item.section, item.field);
		this.cd.detectChanges();

		const elementId = this.resolveStep3ScrollElementId(item);
		scrollPageContainerToStep3ReviewTargetWhenReady(elementId);
	}

	onStepperNextSignOffClick(stepper: MatStepper): void {
		if (!this.isStep3CoreReviewComplete) {
			this.step3GapsChecklistExpanded = true;
			this.signOffForm?.controls?.forEach((ctrl) => ctrl.markAllAsTouched());
			this.cd.markForCheck();
			return;
		}
		this.onStepperNextSignOff(stepper);
	}

	private resolveStep3ScrollElementId(item: Step3ReviewIncompleteItem): string {
		if (item.section === "competency" && item.field) {
			return step3ReviewScrollTargetId("competency", item.field);
		}
		if (item.section === "outcome" && item.field && item.field !== "outcome") {
			return step3ReviewScrollTargetId("outcome", item.field);
		}
		if (item.section === "reminderNotes" && item.field && item.field !== "reminderStatus") {
			return step3ReviewScrollTargetId("reminderNotes", item.field);
		}
		if (item.section === "maneuver" && item.field) {
			const maneuverCode = item.field.split(":")[0];
			if (maneuverCode) return step3ReviewScrollTargetId("maneuver", maneuverCode);
		}
		return step3ReviewScrollTargetId(item.section);
	}

	private pinStep3ScrollHighlightFromChecklist(item: Step3ReviewIncompleteItem): void {
		this.step3ScrollHighlight = {
			candidateIndex: item.candidateIndex,
			key: this.step3IncompleteItemKey(item),
		};
		this.cd.markForCheck();
	}

	private step3IncompleteItemKey(item: Step3ReviewIncompleteItem): string {
		return item.field ? `${item.section}:${item.field}` : item.section;
	}

	private syncStep3ScrollHighlightIfResolved(): void {
		if (!this.step3ScrollHighlight) return;

		const stillIncomplete = this.step3ReviewIncompleteItems.some(
			(item) =>
				item.candidateIndex === this.step3ScrollHighlight!.candidateIndex &&
				this.step3IncompleteItemKey(item) === this.step3ScrollHighlight!.key,
		);

		if (!stillIncomplete) {
			this.step3ScrollHighlight = null;
		}
	}

	get step3MissingObComments(): Step3MissingObComment[] {
		return getStep3MissingObComments(this.step3ReviewValidationParams());
	}

	private openIncompleteObCommentsModal(missing: Step3MissingObComment[]): void {
		const groups: Array<{
			candidateName: string;
			items: Array<{ maneuver: string; ob: string; role: string; action: boolean }>;
		}> = [];

		for (const row of missing) {
			const role = row.candidateRole === CandidateRole.PF ? "PF" : row.candidateRole === CandidateRole.PM ? "PM" : "";
			const item = {
				maneuver: row.maneuverLabel?.trim() || "",
				ob: row.obLabel?.trim() || "OB",
				role,
				action: row.action,
			};

			const existing = groups.find((g) => g.candidateName === row.candidateName);
			if (existing) {
				existing.items.push(item);
			} else {
				groups.push({ candidateName: row.candidateName, items: [item] });
			}
		}

		this.dialog.open(CbtaModalComponent, {
			panelClass: ["error-modal", "incomplete-obs-modal"],
			height: "auto",
			maxHeight: "90vh",
			width: "520px",
			autoFocus: "dialog",
			hasBackdrop: true,
			data: {
				modalName: "incompleteObsModal",
				modalTitle: "Incomplete OBs Detected",
				modalContent: {
					message:
						"Comments are required for all OBs prior to final sign-off. The system has detected missing comments on one or more OBs. Please update the required fields to proceed with the sign-off.",
					groups,
				},
				modalBtnConfirmText: "Close",
				noCloseBtn: true,
			},
		});
	}

	/** Ensure sign-off subforms exist, then refresh Rating A/B/C from the current draft. */
	ensureStep3SignOffForms(): void {
		const formsReady = this.signOffForm?.length === this.candidateList.length && this.candidateList.length > 0;
		if (!formsReady) {
			this.rebuildTs1SignOffFormsIfNeeded();
			return;
		}
		this.patchPendingSignOffRatings();
	}

	/** Write a fresh offline calculation onto existing sign-off controls. */
	private patchPendingSignOffRatings(): void {
		for (const control of this.signOffForm.controls) {
			const candidateId = control.get("candidateId")?.value;
			if (!candidateId) continue;
			const result = this.offlineRatingService.calculate(candidateId);
			control.patchValue(
				{
					ratingA: result?.ratingA ?? 1,
					ratingB: result?.ratingB ?? 1,
					ratingC: result?.ratingC ?? 1,
				},
				{ emitEvent: false },
			);
		}
	}

	onStepperSelectionChange(selectedIndex: number): void {
		if (this.signOffDraftSaveTimer) {
			clearTimeout(this.signOffDraftSaveTimer);
			this.signOffDraftSaveTimer = null;
			void this.saveSignOffDraft();
		}
		this.syncSignOffFormToDraftMemory();

		this.cd.markForCheck();
		if (selectedIndex === 2) {
			this.ensureStep3SignOffForms();
			this.invalidateReviewCaches();
			this.cd.markForCheck();
			this.scrollToTop();
		}
		if (selectedIndex === 3) {
			this.ensureStep3SignOffForms();
			this.cd.markForCheck();
			this.scrollToTop();
		}
		if (selectedIndex === 1) {
			// Step 2 remounts — hydrate summary after ViewChild binds.
			this.queueRepeatDisabledSync();
			this.cd.markForCheck();
			this.scrollToTop();
			setTimeout(() => this.refreshStep2IfActive(), 0);
		}
	}

	/**
	 * Navigate immediately on Back. Base waits for upsertDraft → HTTP sync before
	 * stepper.previous(), which freezes Step 3 → Step 2 with large drafts.
	 */
	override onStepBack(stepper: MatStepper): void {
		const prevIndex = Math.max(0, stepper.selectedIndex - 1);
		const prevPage = prevIndex + 1;

		if (this.signOffDraftSaveTimer) {
			clearTimeout(this.signOffDraftSaveTimer);
			this.signOffDraftSaveTimer = null;
		}
		this.syncSignOffFormToDraftMemory();

		const signOffPatch = this.signOffForm?.length > 0 ? { signOffDetails: this.collectSignOffDetailsFromForm() } : {};

		this.containerRatingsService.silenceAutosave = true;
		this.continuePage = prevPage;
		stepper.previous();

		void this.containerRatingsService
			.upsertDraft(this.assessmentOfflineId, signOffPatch, prevPage, undefined, { forcePage: true })
			.finally(() => {
				setTimeout(() => (this.containerRatingsService.silenceAutosave = false), 250);
			});
	}

	ngAfterViewInit(): void {
		this.queueRepeatDisabledSync();
		this.setupBaseContainerWidthObserver();
	}

	override ngOnDestroy(): void {
		this.repeatDisabledSyncQueued = false;
		if (this.signOffDraftSaveTimer) {
			clearTimeout(this.signOffDraftSaveTimer);
			this.signOffDraftSaveTimer = null;
		}
		this.baseContainerWidthDestroyed = true;
		this.baseContainerWidthSyncQueued = false;
		this.baseContainerResizeObserver?.disconnect();
		this.baseContainerResizeObserver = undefined;
		this.containerRatingsService.silenceAutosave = false;
		super.ngOnDestroy();
	}

	/** Observe #baseContainer and cache width after the current CD turn to avoid NG0100. */
	private setupBaseContainerWidthObserver(): void {
		if (this.baseContainerWidthDestroyed) {
			return;
		}
		const el = this.baseContainer?.nativeElement;
		if (!el || typeof ResizeObserver === "undefined") {
			this.queueBaseContainerWidthSync();
			return;
		}
		this.baseContainerResizeObserver?.disconnect();
		this.baseContainerResizeObserver = new ResizeObserver(() => this.queueBaseContainerWidthSync());
		this.baseContainerResizeObserver.observe(el);
		this.queueBaseContainerWidthSync();
	}

	private queueBaseContainerWidthSync(): void {
		if (this.baseContainerWidthDestroyed || this.baseContainerWidthSyncQueued) {
			return;
		}
		this.baseContainerWidthSyncQueued = true;
		queueMicrotask(() => {
			this.baseContainerWidthSyncQueued = false;
			if (this.baseContainerWidthDestroyed) {
				return;
			}
			this.syncBaseContainerWidth();
		});
	}

	private syncBaseContainerWidth(): void {
		if (this.baseContainerWidthDestroyed) {
			return;
		}
		const next = this.baseContainer?.nativeElement.offsetWidth ?? 0;
		if (this.baseContainerWidthPx === next) {
			return;
		}
		this.baseContainerWidthPx = next;
		this.cd.markForCheck();
	}

	navigateToReviewStep(): void {
		if (this.stepper.selectedIndex < 1) return;
		this.ensureStep3SignOffForms();
		this.stepper.selectedIndex = 2;
		this.cd.markForCheck();
	}

	get currentSignOffControl(): UntypedFormGroup | null {
		const ctrl = this.signOffForm?.at(this.current) as UntypedFormGroup | undefined;
		return ctrl ?? null;
	}

	get reviewManeuverRows(): Ts1ReviewManeuverRow[] {
		return this.buildReviewManeuverRowsForIndex(this.current);
	}

	get reviewSummaryComments(): OBComments[] {
		return this.buildReviewSummaryCommentsForIndex(this.current);
	}

	get reviewTraineeAssessmentId(): string {
		return this.getReviewTraineeAssessmentIdForIndex(this.current);
	}

	/** TS-1 v4 rating shell opts in. v3 keeps the existing session list and reminder notes. */
	protected readonly useEts1AocSessions: boolean = false;

	get ets1SessionVersion(): number {
		return Number(
			this.rawFormData?.ets1SessionVersion ?? this.containerRatingsService.draft?.formDetails?.ets1SessionVersion ?? 1,
		);
	}

	get reminderNotesEnabledForForm(): boolean {
		if (isEts1SessionV2({ ets1SessionVersion: this.ets1SessionVersion })) {
			return (this.rawFormData?.reminderNotes?.length ?? 0) > 0;
		}
		return !!(
			this.rawFormData?.reminderNotesEnabled ?? this.containerRatingsService.draft?.formDetails?.reminderNotesEnabled
		);
	}

	get showEts1ReminderNotes(): boolean {
		return isEts1SessionV2(this.rawFormData) && (this.rawFormData?.reminderNotes?.length ?? 0) > 0;
	}

	get currentReminderNoteResponses(): any {
		return this.currentSignOffControl?.get("reminderNoteResponses") ?? null;
	}

	protected override async goToTrainingPage(page: number): Promise<void> {
		this.continuePage = page;

		if (page === 2) {
			await this.onStepperNext(this.stepper as MatStepper);
			return;
		}
		if (page === 3) {
			await this.onStepperNext(this.stepper as MatStepper);
			this.onStepperNextReview(this.stepper as MatStepper, true);
			return;
		}
		if (page >= 4) {
			await this.onStepperNext(this.stepper as MatStepper);
			this.onStepperNextReview(this.stepper as MatStepper, true);
			this.onStepperNextSignOff(this.stepper as MatStepper, true);
		}
	}

	/**
	 * Step 3 → Step 4 (Sign Off) transition. Optionally bypass validation while restoring drafts.
	 */
	override onStepperNextSignOff(_stepper: MatStepper, bypassValidation: boolean = false) {
		this.loadingService.setLoading(true);
		this.ensureStep3SignOffForms();

		if (!bypassValidation && !this.isStep3CoreReviewComplete) {
			this.step3GapsChecklistExpanded = true;
			this.signOffForm?.controls?.forEach((ctrl) => ctrl.markAllAsTouched());
			this.loadingService.setLoading(false);
			this.cd.markForCheck();
			return;
		}

		if (!bypassValidation) {
			const missingObs = this.step3MissingObComments;
			if (missingObs.length > 0) {
				this.openIncompleteObCommentsModal(missingObs);
				this.loadingService.setLoading(false);
				return;
			}
		}

		this.isStep2FormCreated = true;
		this.current = 0;
		this.prev = -1;
		this.continuePage = Math.max(this.continuePage, 4);

		if (!bypassValidation) {
			if (this.signOffDraftSaveTimer) {
				clearTimeout(this.signOffDraftSaveTimer);
				this.signOffDraftSaveTimer = null;
			}
			this.syncSignOffFormToDraftMemory();
			void this.saveSignOffDraft();
		}

		if (bypassValidation) this.forceSelectStep(3);
		else this.stepper.next();

		this.loadingService.setLoading(false);
	}

	get canSendToCandidate(): boolean {
		return canSendTs1V3ToCandidate(this.signOffForm?.getRawValue?.() ?? []) && !this.isSendingToCandidate;
	}

	async onSendToCandidate(_stepper: MatStepper): Promise<void> {
		if (!navigator.onLine) {
			this.containerRatingsService.blockSubmitIfOffline(true);
			return;
		}

		if (!this.canSendToCandidate || this.isSendingToCandidate) return;
		if (!(await this.confirmSendToCandidateAction())) return;

		this.isSendingToCandidate = true;
		this.cd.markForCheck();
		// Yield so the in-page loader / disabled button paint before heavy draft work.
		await new Promise<void>((resolve) => setTimeout(resolve, 0));
		this.signOffMode = "remote";

		try {
			if (this.signOffDraftSaveTimer) {
				clearTimeout(this.signOffDraftSaveTimer);
				this.signOffDraftSaveTimer = null;
			}
			await this.saveSignOffDraft();
			this.containerRatingsService.updateSelectedDraft({ signOffMode: "remote" } as Partial<DraftItem>);

			const signoff = this.signOffForm.at(0).value as {
				trainerName: string;
				instructorLicenseNumber?: string;
				useInstructorLicense?: boolean;
				trainerSignature: File | Blob | string;
			};

			const candidate = this.ratingForm.value as Array<{
				observations: Array<{ traineeAssessmentId?: string }>;
			}>;
			const trainingAssessmentId = (candidate?.[0]?.observations?.[0]?.traineeAssessmentId as string) || "";

			if (!trainingAssessmentId) {
				this.isSendingToCandidate = false;
				this.cd.markForCheck();
				return;
			}

			let instructor_license_number = signoff.instructorLicenseNumber;
			if (!instructor_license_number?.trim()) {
				const useInstructorLicense = signoff.useInstructorLicense ?? true;
				instructor_license_number = useInstructorLicense
					? (this.profileService?.profile?.pilot_license_number ?? "")
					: (this.profileService?.profile?.examiner_number ?? "");
			}

			const draftId = this.containerRatingsService.draft?.id || trainingAssessmentId;
			if (this.containerRatingsService.inDraftMode && draftId) {
				const syncResult = await this.containerRatingsService.flushStagingSync(draftId);

				if (syncResult.status === "offline") {
					this.isSendingToCandidate = false;
					this.cd.markForCheck();
					this.containerRatingsService.blockSubmitIfOffline(true);
					return;
				}
				if (syncResult.status !== "synced") {
					this.isSendingToCandidate = false;
					this.cd.markForCheck();
					this.containerRatingsService.presentStagingSyncError(syncResult.error);
					return;
				}
			}

			this.httpService
				.sendToCandidate({
					trainingAssessmentId,
					instructor_name: signoff.trainerName,
					instructor_license_number: String(instructor_license_number ?? "").trim(),
					instructor_signature: signoff.trainerSignature,
				})
				.subscribe({
					next: async () => {
						this.isSendingToCandidate = false;
						this.cd.markForCheck();
						if (this.containerRatingsService.draft?.id) {
							await this.containerRatingsService.deleteDraft(this.containerRatingsService.draft.id);
						}
						this.router.navigate([RouteType.PENDING]);
					},
					error: (err) => {
						this.isSendingToCandidate = false;
						this.cd.markForCheck();
						this.onErrorSignOff(err);
					},
				});
		} catch (err) {
			this.isSendingToCandidate = false;
			this.cd.markForCheck();
			console.error("onSendToCandidate failed:", err);
			this.onErrorSignOff(err);
		}
	}

	// Public wrappers for template navigation that override base class methods.
	override onPrev() {
		const previous = this.current;
		super.onPrev();
		if (this.current !== previous) this.scrollToTop();
	}
	override onNext() {
		const previous = this.current;
		super.onNext();
		if (this.current !== previous) this.scrollToTop();
	}
	override isLeftTransition(idx: number): boolean {
		return super.isLeftTransition(idx);
	}

	/**
	 * Return to dashboard route.
	 */
	goback() {
		this.router.navigate([RouteType.DASHBOARD]);
	}

	/**
	 * Patch Step 1 from draft values and ensure candidate rows match.
	 */
	protected override loadDraftTrainingInfo(draft: DraftItem) {
		const trainingInfo = draft.trainingInformation;
		const targetCount = trainingInfo.candidates.length;

		while ((this.candidates?.length ?? 0) < targetCount) {
			const cg = createCandidateGroup(this.fb, this.cvs, CONSTANT.FORM_TYPE.ts_1, {
				splitAircraftSimulatorRegistration: true,
				ts1V3FlightDetails: true,
			});
			attachSessionFormControls(cg, this.fb, this.sessionKeys, this.formSessionDetails);
			this.applyFormTemplateSessionRemarks(cg);
			this.candidates.push(cg, { emitEvent: false });
			this.candidatePanelExpanded.push(true);
		}

		const toNestedSessions = (cand: any) => {
			const selected = new Set<string>([
				...(cand?.sessionDetails?.[0]?.ts_1 ?? []),
				...(cand?.sessionDetails?.[0]?.coursePurpose ?? []),
				...(cand?.sessionDetails?.[0]?.qualification ?? cand?.sessionDetails?.[0]?.ts_1 ?? []),
			]);
			const nested: any = {};
			for (const section of this.sessionKeys ?? []) {
				const sectionCode = section?.name?.code;
				nested[sectionCode] = {};
				for (const d of section?.details ?? []) {
					const code = typeof d === "string" ? d : d.code;
					nested[sectionCode][code] = selected.has(code);
				}
			}
			return nested;
		};

		this.candidates.patchValue(
			trainingInfo.candidates.map((c: any) => {
				const rankOption = this.asOption(this.sharedService.dropDownOptionsRank, c?.rank);
				const trainingProfileOption = this.asOption(this.sharedService.dropDownOptionsPilotProfile, c?.trainingProfile);

				return {
					name: c.name,
					rank: rankOption,
					staffNumber: c.staffNumber,
					licenseNo: c.licenseNumber,
					licenseType: c.licenseType,
					pilotProfile: trainingProfileOption,
					trainingProfileOther: c.trainingProfileOther ?? "",
					licenseExpiryDate: c.licenseExpiryDate,
					sessionDetails: toNestedSessions(c),
					sessionRemarks: this.toNestedSessionRemarks(c),
					flightSimulatorDetails: flightSimulatorDetailsForFormPatch(
						c.flightSimulatorDetails ?? {},
						this.sharedService.dropDownOptionsAircraftType,
					),
				};
			}),
			{ emitEvent: true },
		);

		(this.candidates.controls as UntypedFormGroup[]).forEach((fg, index) => {
			const fs = fg.get("flightSimulatorDetails") as UntypedFormGroup | null;
			const source = (trainingInfo.candidates?.[index]?.flightSimulatorDetails ?? {}) as Record<string, unknown>;
			if (fs) {
				patchFlightSimulatorDetailsOntoForm(
					fs,
					flightSimulatorDetailsForFormPatch(source, this.sharedService.dropDownOptionsAircraftType) as Record<
						string,
						unknown
					>,
					{ required: index === 0 },
				);
			}
		});

		// 2) Immediately enforce disable/clear rules per row
		(this.candidates.controls as UntypedFormGroup[]).forEach((fg) => {
			const rankVal = fg.get("rank")?.value;
			// handle either string rank or {code: string}
			const code = typeof rankVal === "string" ? rankVal : rankVal?.code;

			const shouldDisable = code === CONSTANT.RANK.pur || code === CONSTANT.RANK.scc;

			const licenseNo = fg.get("licenseNo");
			const licenseType = fg.get("licenseType");
			const licenseExpiryDate = fg.get("licenseExpiryDate");

			if (shouldDisable) {
				// Clear first (safer for mat-autocomplete) then disable
				licenseNo?.setValue(null, { emitEvent: false });
				licenseNo?.disable({ emitEvent: false });

				licenseType?.setValue(null, { emitEvent: false });
				licenseType?.disable({ emitEvent: false });

				licenseExpiryDate?.setValue(null, { emitEvent: false });
				licenseExpiryDate?.disable({ emitEvent: false });
			} else {
				licenseNo?.enable({ emitEvent: false });
				licenseType?.enable({ emitEvent: false });
				licenseExpiryDate?.enable({ emitEvent: false });
			}
		});

		this.syncSharedFlightSimToAllCandidates();
		this.relaxSecondaryFlightSimValidation();
		this.ts1TrainingInfoForm.updateValueAndValidity({ emitEvent: false });
	}

	/**
	 * Copy primary candidate flight/simulator fields to secondary rows.
	 * Step 1 v3 renders one shared flight block bound to candidate 0 only.
	 */
	private syncSharedFlightSimToAllCandidates(): void {
		if (this.candidates.length < 2) return;

		const primaryFs = this.candidates.at(0)?.get("flightSimulatorDetails") as UntypedFormGroup | null;
		if (!primaryFs) return;

		const raw = primaryFs.getRawValue() as Record<string, unknown>;
		for (let i = 1; i < this.candidates.length; i++) {
			const fs = this.candidates.at(i)?.get("flightSimulatorDetails") as UntypedFormGroup | null;
			if (!fs) continue;
			patchFlightSimulatorDetailsOntoForm(fs, raw, { required: false });
			fs.updateValueAndValidity({ emitEvent: false });
		}
	}

	/**
	 * Secondary candidates do not expose flight/simulator fields in the v3 UI.
	 * Clear their hidden validators so Step 1 validity follows the shared block on candidate 0.
	 */
	private relaxSecondaryFlightSimValidation(): void {
		for (let i = 1; i < this.candidates.length; i++) {
			const fs = this.candidates.at(i)?.get("flightSimulatorDetails") as UntypedFormGroup | null;
			if (!fs) continue;

			const clearDeep = (ctrl: AbstractControl) => {
				ctrl.clearValidators();
				if (ctrl instanceof UntypedFormGroup || ctrl instanceof UntypedFormArray) {
					Object.values(ctrl.controls).forEach((child) => clearDeep(child));
				}
				ctrl.updateValueAndValidity({ emitEvent: false });
			};
			Object.values(fs.controls).forEach((ctrl) => clearDeep(ctrl));
			fs.updateValueAndValidity({ emitEvent: false });
		}
	}

	isCandidatePanelExpanded(index: number): boolean {
		return this.candidatePanelExpanded[index] ?? true;
	}

	setCandidatePanelExpanded(index: number, expanded: boolean): void {
		while (this.candidatePanelExpanded.length <= index) {
			this.candidatePanelExpanded.push(true);
		}
		this.candidatePanelExpanded[index] = expanded;
	}

	/**
	 * Add a new candidate row and persist the change.
	 */
	async addNewCandidate() {
		if (!this.canAddCandidate()) return;

		const newIndex = this.candidates.length;
		const group = createCandidateGroup(this.fb, this.cvs, CONSTANT.FORM_TYPE.ts_1, {
			splitAircraftSimulatorRegistration: true,
			ts1V3FlightDetails: true,
		});
		attachSessionFormControls(group, this.fb, this.sessionKeys, this.formSessionDetails);
		this.applyFormTemplateSessionRemarks(group);
		this.candidates.push(group, { emitEvent: false });
		this.candidatePanelExpanded.push(true);

		this.syncSharedFlightSimToAllCandidates();
		this.relaxSecondaryFlightSimValidation();
		this.ts1TrainingInfoForm.updateValueAndValidity({ emitEvent: false });
		this.cd.detectChanges();

		this.isStep2FormCreated = false;
		await this.saveTrainingDraft();
		scrollPageContainerToCandidateHeaderWhenReady(newIndex);
	}

	/**
	 * Remove the candidate at the provided index and keep all steps in sync.
	 */
	async removeCandidate(idx: number) {
		this.containerRatingsService.silenceAutosave = true;

		try {
			await this.containerRatingsService.deleteCandidateForSelectedDraft(idx);
			this.candidates.removeAt(idx, { emitEvent: false });
			this.candidatePanelExpanded.splice(idx, 1);

			if (this.ratingForm?.length > idx) {
				this.ratingForm.removeAt(idx, { emitEvent: false });
			}
			if (this.signOffForm?.length > idx) {
				this.signOffForm.removeAt(idx, { emitEvent: false });
			}

			this.candidateList = this.getCandidateList();
			this.normalizeCandidateView();
			this.isStep2FormCreated = false;

			await this.saveTrainingDraft();

			this.ts1TrainingInfoForm.updateValueAndValidity({ emitEvent: false });
			this.ts1RatingForm.updateValueAndValidity({ emitEvent: false });
			this.ts1SignOffForm.updateValueAndValidity({ emitEvent: false });
			this.cd.detectChanges();
		} finally {
			this.containerRatingsService.silenceAutosave = false;
		}
	}
}
